// The system map (/help/system-map and /embed/system-map): every part of Tap
// Leagues as people see it, grouped into areas, the areas into a few groups,
// with what holds what and what connects across areas. One file so it can be
// kept up to date when a feature ships; the page draws whatever is here.
//
// Group:   {id, name, art, tint, ink, areas} see SYSTEM_MAP_GROUPS below
// Section: {id, name, line, art, format?, admin?} an area
//   line    its one-line description: shown once you are inside the area (under
//           its name in the bar), never on its card
//   format  key into formatExplainers (the five formats keep their own tints)
// Node:    {id, section, parent, name, line, art, aliases, what, holds,
//           sees, manages, rules, related, admin?, siblings?}
//   parent  the node that holds this one (null = the section's root). Parts
//           nest as deep as the product does (a cycle holds divisions, a
//           division holds teams); how many of a thing there are is said in
//           plain words in the dialog, never as a label on a line.
//   admin   an admin feature: only club admins see or manage it (the one checkbox
//           on the page, "Show admin features", hides these)
//   siblings  a part may hold more than five parts only when they truly are
//           siblings (the channels of Communication); the test asks for the flag
//   related ids this one jumps to from its dialog
// A section's root may share the section's id (club, payments…): a #hash deep
// link resolves to the section first, which is where that root sits anyway.
// Link:    {from, to, label, say} a connection across the map. Inside an area
//           it is drawn as a dotted line routed around the other cards; across
//           areas it shows as a chip beside the hovered or selected part
//           (crossAreaLinks) and in the part's dialog. `say` is the whole
//           sentence a chip's tooltip and a dialog row show (every link has
//           one: "Players chats in Chats" is what a label makes on its own);
//           `label` is the short relation word for the line label and search.
// Step:    {from, to} the order of things: `to` CANNOT exist or happen without
//           `from` (SYSTEM_MAP_STEPS). A hard requirement only: never a usual,
//           optional or alternative route (those are links, or said in words).
//           An arrowed connector inside an area, a Needs first / Comes next chip
//           across areas, and both in the dialog.
//
// Words (checked by systemMapCopyProblems and the test): matches (never
// fixtures), Players (never roster), Memberships & passes, Booking rules (never
// area groups), Playing areas, "Membership or pass needed" (never members only),
// Tap Leagues (never super admin), no walk-ins, no "N days" placeholders.
// Search aliases may use the old words so people still find things.

import {formatExplainers} from '~/utils/formatExplainers';

export const SYSTEM_MAP_SECTIONS = [
  {id: 'club', name: 'Club', line: 'Its settings, how people join and what is on its page.', art: 'clubhouse'},
  {id: 'people', name: 'People & accounts', line: 'Players, their accounts, signing up, privacy, following.', art: 'people'},
  {id: 'leagues', name: 'Box leagues', format: 'league', line: 'Divisions by strength. Play everyone, move up or down.', art: 'table'},
  {id: 'ladders', name: 'Ladders', format: 'ladder', line: 'One ranked list. Challenge above you, win, take their spot.', art: 'ladder'},
  {id: 'tournaments', name: 'Tournaments', format: 'tournament', line: 'Brackets, group stages and one name on the trophy.', art: 'trophy'},
  {id: 'americano', name: 'Americano leagues', format: 'americano', line: 'Enter solo, partner everyone once, points count for you.', art: 'swap'},
  {id: 'meets', name: 'Meets', format: 'meet', line: 'Recurring social sessions. RSVP, turn up, play.', art: 'handshake'},
  {id: 'matches', name: 'Matches & scoring', line: 'One match record for all formats: scored, approved, counted.', art: 'scoreboard'},
  {id: 'payments', name: 'Payments', line: 'Entry and session fees into the club\'s own Stripe account.', art: 'terminal'},
  {id: 'memberships', name: 'Memberships & passes', line: 'Types a club sells or gives away, applications, renewals.', art: 'membercard'},
  {id: 'bookings', name: 'Playing areas & bookings', line: 'Courts, tables, boards: who can book, when, for how much.', art: 'court'},
  {id: 'communication', name: 'Communication', line: 'Notifications, chats, announcements, email, the home feed.', art: 'chat'},
  {id: 'website', name: 'Widgets', line: 'Widgets and embed codes for the club\'s own site.', art: 'globe'}, // X146: the id (and #website hash) stays
  {id: 'tv', name: 'Clubhouse TV', line: 'Live clubhouse screens: results, coming up, how to join.', art: 'tv'},
  {id: 'admin', name: 'Admin tools', admin: true, line: 'Dashboard, players, imports, reports, the setup assistant.', art: 'wrench'},
];

// Group: {id, name, art, tint, ink, areas} the first thing the map shows: a few
// big cards, each a handful of areas (Grant 2026-10-25: the areas all at once
// were overwhelming). Every area is in exactly one group (validateSystemMap).
// A group's id is a #hash deep link like an area's; it never clashes with a
// section's or a part's id. The tints are quiet and light; the areas inside keep
// their own.
export const SYSTEM_MAP_GROUPS = [
  {id: 'competition-types', name: 'Competition types', art: 'ball', tint: '#EAF2FB', ink: '#2F5F96', areas: ['leagues', 'ladders', 'tournaments', 'americano', 'meets']},
  {id: 'playing', name: 'Playing', art: 'whistle', tint: '#EAF6EE', ink: '#2E7A4E', areas: ['matches', 'communication']},
  {id: 'running-your-club', name: 'Running your club', art: 'clipboard', tint: '#F8F1E7', ink: '#8A5A22', areas: ['club', 'people', 'payments', 'memberships', 'bookings', 'admin']},
  {id: 'showing-it-off', name: 'Showing it off', art: 'megaphone', tint: '#F3EEF8', ink: '#6A4399', areas: ['website', 'tv']},
];

export const SYSTEM_MAP_NODES = [
  // ── Club ────────────────────────────────────────────────────────────────
  {
    id: 'club', section: 'club', parent: null, name: 'Club', art: 'clubhouse',
    line: 'The place everything belongs to.',
    aliases: ['venue', 'organisation', 'society'],
    what: 'A club is the home of its players, competitions, playing areas, payments and memberships. Somebody creates it, becomes its first admin, and shares an invite link.',
    holds: ['Players and admins', 'Competitions in five formats', 'Playing areas and bookings', 'A payments account, memberships and passes', 'Chats, announcements, widgets and TV displays'],
    sees: 'Anyone finds a listed club; what they see depends on Who can see your club.',
    manages: 'Club admins',
    rules: ['Who can see your club: Public, Club players only, or Club players only with the player list needing a membership (new clubs start as Club players only).', 'Listed in Find a club is a separate switch.', 'A club is archived, never deleted, once it has history.'],
    related: ['club-settings', 'players', 'joining', 'club-page', 'find-club'],
  },
  {
    id: 'club-settings', section: 'club', parent: 'club', name: 'Club settings', art: 'wrench', admin: true,
    line: 'Name, place, logo, brand colour, time zone, website, visibility.',
    aliases: ['visibility', 'privacy', 'logo', 'brand colour', 'time zone', 'listed in search'],
    what: 'The club\'s own details and the switches that shape everything else.',
    holds: ['Name, description, address and map pin', 'Logo and brand colour', 'Time zone (every match time is shown in it)', 'Website address, with verification', 'Who can see your club and whether it is listed'],
    sees: 'Club admins', manages: 'Club admins',
    rules: ['Payments, memberships, bookings and the setup assistant are switched on for a club by Tap Leagues.', 'Hide player details hides emails and phone numbers from other players.'],
    related: ['club-visibility', 'website-verification'],
  },
  {
    id: 'club-visibility', section: 'club', parent: 'club-settings', name: 'Who can see your club', art: 'eye', admin: true,
    line: 'Public, Club players only, or players need a membership.',
    aliases: ['members only', 'public club', 'private club', 'listed'],
    what: 'One choice that decides what a stranger sees of the club and who can see its players.',
    holds: ['Public: anyone opens the club and its competitions', 'Club players only: strangers see the name, logo, city, description and how to join', 'Club players only, player list needs a membership: the players list, club-wide chats and name search need a current membership or pass'],
    sees: 'Club admins', manages: 'Club admins',
    rules: ['Every competition can be more private than its club (Public or Club players only), never more open.', 'Widgets and TV screens show what a member would see.', 'Making the player list need a membership gives players a grace period, with a reminder near its end.'],
    related: ['profile-privacy', 'membership-settings'],
  },
  {
    id: 'club-admins', section: 'club', parent: 'club-settings', name: 'Club admins', art: 'whistle',
    line: 'The people who run the club.',
    aliases: ['organiser', 'committee', 'administrator'],
    what: 'Any player in the club can be made an admin. Admins manage everything in the club; some may also see health details.',
    holds: ['Admin flag per player', 'Can see health details (memberships) per admin'],
    sees: 'Everyone sees who the admins are', manages: 'Club admins',
    rules: ['Unlimited admins.', 'An admin who also plays is treated as a player in their own matches and challenges.'],
    related: ['players', 'admin'],
  },
  {
    id: 'sports', section: 'club', parent: 'club-settings', name: 'Sports', art: 'ball',
    line: 'Every competition picks a sport.',
    aliases: ['squash', 'tennis', 'padel', 'badminton', 'table tennis', 'snooker', 'pool', 'darts', 'pickleball', 'bowls'],
    what: 'A sport sets the words and defaults a competition uses.',
    holds: ['What a playing area is called (court, table, board, lane)', 'Default match length', 'Stock scoring systems and handicap scheme', 'Squash Levels export (squash only)'],
    sees: 'Everyone', manages: 'Tap Leagues',
    rules: ['One sport per competition; a club can run several sports.'],
    related: ['scoring-systems', 'handicaps'],
  },
  {
    id: 'club-page', section: 'club', parent: 'club', name: 'Club page', art: 'globe',
    line: 'The club on tapleagues.com.',
    aliases: ['public page', 'profile page', 'clubs/'],
    what: 'The club\'s own page: competitions by format, players, memberships and passes, playing areas, how to join.',
    holds: ['Competitions, live and ended', 'Players (when visible)', 'Invite link, QR poster and invite settings', 'Memberships & passes to join'],
    sees: 'Depends on Who can see your club', manages: 'Club admins',
    rules: ['A locked page still shows the harmless facts: name, logo, city, description and how to join.'],
    related: ['club-visibility', 'join-link', 'public-pages'],
  },
  {
    id: 'find-club', section: 'club', parent: 'club-page', name: 'Find a club', art: 'search',
    line: 'Search by name, or see clubs near you.',
    aliases: ['club search', 'nearby clubs', 'search clubs', 'listed', 'discover', 'find a club'],
    what: 'Find a club by name, or by a place or your location to see clubs nearby. Each club decides whether it is listed.',
    holds: ['Search by name', 'Nearby clubs from a place or your location', 'Each club\'s page, with how to join'],
    sees: 'Anyone', manages: 'Club admins (listed or not)',
    rules: ['Only listed clubs appear; an unlisted club is still reachable by its link.'],
    related: ['club-page', 'joining', 'club-settings'],
  },
  {
    id: 'joining', section: 'club', parent: 'club', name: 'Joining', art: 'door',
    line: 'How people get into the club and its competitions.',
    aliases: ['invite settings', 'join settings', 'invite', 'sign up', 'access'],
    what: 'Every club and competition has one managed invite link, invite settings that say who may join and whether an admin approves, and a waiting area for joiners who need placing.',
    holds: ['Invite link and QR poster', 'Invite settings', 'Join requests', 'Waiting area', 'Entry questions'],
    sees: 'Everyone sees the link when it is visible to them', manages: 'Club admins',
    rules: ['Joining a competition also joins the club.', 'Defaults for a new link: Anyone, join instantly, link visible to club players only.'],
    related: ['join-link', 'waiting-area', 'join-requests'],
  },
  {
    id: 'join-link', section: 'club', parent: 'joining', name: 'Invite link & QR poster', art: 'qr',
    line: 'One managed link per club and competition.',
    aliases: ['join link', 'qr code', 'poster', 'slug', 'short link', 'share'],
    what: 'A link anyone can be sent, with a printable QR poster. Its settings decide who may use it and what happens when they do.',
    holds: ['Who can join: Anyone, Club players, Nobody', 'Join instantly or each request approved', 'Password, usage limit, start and end dates, welcome message', 'On the page: visible, club players only, or hidden'],
    sees: 'Everyone (when visible on the page)', manages: 'Club admins',
    rules: ['Regenerate the link and the old one stops working.', 'A link shown to club players only is hidden from everyone else, but someone sent it directly can still use it.', 'Open meets default to club players only + anyone + instant.'],
    related: ['join-requests', 'waiting-area', 'short-links'],
  },
  {
    id: 'join-requests', section: 'club', parent: 'joining', name: 'Join requests', art: 'clipboard', admin: true,
    line: 'Approve or decline when a link needs approval.',
    aliases: ['access request', 'approval', 'pending'],
    what: 'When an invite link requires approval, each request waits here with the person\'s message and, for paid competitions, the fee they were quoted.',
    holds: ['Message from the joiner', 'Quoted entry fee and terms version'],
    sees: 'Club admins', manages: 'Club admins',
    rules: ['Approval places directly only in singles competitions; everything else goes to the waiting area.', 'Admins are notified of each request.'],
    related: ['waiting-area', 'entry-fees'],
  },
  {
    id: 'waiting-area', section: 'club', parent: 'join-requests', name: 'Waiting area', art: 'hourglass',
    line: 'Joiners held until an admin places them.',
    aliases: ['pending placement', 'placement', 'unplaced', 'partner preference'],
    what: 'People who joined a league cycle, americano cycle or (optionally) a ladder wait here until an admin puts them in a box, a seat or a position.',
    holds: ['Who is waiting, with their message', 'Partner preferences (who they would like to team up with)', 'Suggested groupings for a full box of newcomers'],
    sees: 'The person waiting and club admins', manages: 'Club admins',
    rules: ['League and americano cycles always hold joiners; ladders have a switch.', 'Place completes a short team: a waiting player can complete a doubles pair.', 'Everyone is told when they have been placed.'],
    related: ['league-division', 'americano-seats', 'ladder-positions'],
  },
  {
    id: 'placed', section: 'club', parent: 'waiting-area', name: 'Placed in a competition', art: 'check',
    line: 'From the waiting area into a box, a seat or a position.',
    aliases: ['placement', 'placed', 'put in a box', 'assigned'],
    what: 'The moment an admin puts a waiting joiner into a league division, an americano seat or a ladder position. From then on they play.',
    holds: ['Which division, seat or position', 'Who was placed with whom (partner preferences honoured where possible)', 'The message telling them'],
    sees: 'The person placed and club admins', manages: 'Club admins',
    rules: ['Everyone is told when they have been placed.', 'Placing a waiting player can complete a short doubles team.'],
    related: ['waiting-area', 'league-division', 'americano-seats', 'ladder-positions'],
  },
  {
    id: 'entry-questions', section: 'club', parent: 'join-link', name: 'Entry questions', art: 'clipboard',
    line: 'Yes/no questions asked when someone joins.',
    aliases: ['competition questions', 'join questions'],
    what: 'Short questions a joiner answers about a kind of competition (for example agreeing to play within the cycle window). Answers are kept per person and competition kind.',
    holds: ['Question key, kind of competition, answer'],
    sees: 'The person and club admins', manages: 'Club admins',
    rules: ['Closing the questions dialog counts as a no.'],
    related: ['join-link'],
  },
  {
    id: 'setup-guide', section: 'club', parent: 'club', name: 'Setup checklist & goals', art: 'flag', admin: true,
    line: 'What is left to set up, in the order that suits the club.',
    aliases: ['dashboard checklist', 'to-do', 'getting started', 'setup goals'],
    what: 'The admin dashboard shows what is left to do for a new club, shaped by the goals the admin picked (competitions, memberships, bookings).',
    holds: ['Setup goals', 'Checklist steps with links into the forms', 'Set up with the assistant entry point'],
    sees: 'Club admins', manages: 'Club admins',
    rules: ['Steps disappear as the club fills in.'],
    related: ['admin-dashboard', 'setup-assistant'],
  },

  // ── People & accounts ──────────────────────────────────────────────────
  {
    id: 'players', section: 'people', parent: null, name: 'Players', art: 'people',
    line: 'Everyone in the club.',
    aliases: ['roster', 'members', 'club players', 'member list'],
    what: 'Every person in the club, with or without an account. A player can be in several clubs, hold several memberships or passes, and play in any number of competitions.',
    holds: ['Name, email, phone (hidden when the club hides details)', 'Admin flag and junior flag', 'Account (when they have one)', 'Profile and stats', 'Memberships & passes, teams, bookings, chats, payments'],
    sees: 'Everyone in the club, or only holders of a membership or pass', manages: 'Club admins',
    rules: ['Getting a membership adds the person to the club\'s players; lapsing never removes them.', 'Leaving the club leaves history in place.'],
    related: ['account', 'profile', 'managed-players', 'membership', 'club-admins'],
  },
  {
    id: 'account', section: 'people', parent: 'players', name: 'Account', art: 'key',
    line: 'The sign-in behind a player.',
    aliases: ['user', 'login', 'sign in', 'email', 'password'],
    what: 'An account signs in by email and password or with Apple or Google, and owns one player profile across every club.',
    holds: ['Sign-in methods', 'Two-step sign-in', 'Notification preferences', 'Profile privacy and user tag', 'Consents and accepted terms', 'Saved cards'],
    sees: 'The person', manages: 'The person',
    rules: ['Email is the primary identity; social logins are attached to it.', 'Signing in with Apple or Google never silently creates a second account: link or create is an explicit choice.', 'Duplicate accounts can be combined.'],
    related: ['sign-in-methods', 'two-step', 'notification-preferences', 'profile-privacy', 'combine-accounts', 'saved-cards'],
  },
  {
    id: 'sign-in-methods', section: 'people', parent: 'account', name: 'Sign-in methods', art: 'key',
    line: 'Email & password, Apple, Google.',
    aliases: ['social login', 'apple sign in', 'google sign in', 'external login', 'forgot password'],
    what: 'How the account signs in on web and app.',
    holds: ['Email and password (set or reset by email link)', 'Apple and Google, linked to the email account'],
    sees: 'The person', manages: 'The person',
    rules: ['Apple Hide My Email can never match an existing email, so the chooser asks before creating anything.'],
    related: ['combine-accounts', 'two-step'],
  },
  {
    id: 'two-step', section: 'people', parent: 'sign-in-methods', name: 'Two-step sign-in', art: 'shield',
    line: 'Authenticator app, passkeys, recovery codes.',
    aliases: ['mfa', '2fa', 'totp', 'passkey', 'recovery codes', 'remembered devices', 'security'],
    what: 'A second step after the password: a code from an authenticator app or a passkey. Passkeys can also sign in on the web without a password.',
    holds: ['Authenticator apps and passkeys', 'Recovery codes', 'Remembered devices (30 days)', 'An audit of every change'],
    sees: 'The person', manages: 'The person; Tap Leagues can undo a lock-out',
    rules: ['It can be required for club admins and offered to everyone else.', 'No codes by SMS or email.'],
    related: ['remembered-devices'],
  },
  {
    id: 'remembered-devices', section: 'people', parent: 'two-step', name: 'Remembered devices', art: 'phone',
    line: 'Skip the second step on a device for 30 days.',
    aliases: ['devices', 'trusted device'],
    what: 'Devices where the second step was completed recently. Each can be removed.',
    holds: ['Device name, last used, remembered until'],
    sees: 'The person', manages: 'The person',
    rules: ['Revoking a device asks for the second step again there.'],
    related: ['two-step'],
  },
  {
    id: 'notification-preferences', section: 'people', parent: 'account', name: 'Notification preferences', art: 'bell',
    line: 'Which pushes and emails you get.',
    aliases: ['email preferences', 'push', 'unsubscribe', 'marketing'],
    what: 'Per type of notification, email and push can be turned on or off. Some are the record of something you bought and never switch off.',
    holds: ['One switch per notification type', 'News and updates (off until you say yes)', 'Service updates (on, switchable)'],
    sees: 'The person', manages: 'The person',
    rules: ['Booking and paid-date confirmations always arrive.', 'The unsubscribe link in every email opens these preferences.'],
    related: ['notifications', 'email-preferences'],
  },
  {
    id: 'profile-privacy', section: 'people', parent: 'profile', name: 'Profile privacy', art: 'eye',
    line: 'Public, Club players only, or hidden.',
    aliases: ['private player', 'hidden profile', 'visibility'],
    what: 'Who may open a person\'s profile and stats, and how they appear to strangers.',
    holds: ['Public', 'Club players only (the default)', 'Hidden: every public surface says "Private player"'],
    sees: 'The person', manages: 'The person; the club rule applies to managed players',
    rules: ['Club mates always see each other.', 'Juniors are never public, whatever the setting.'],
    related: ['club-visibility', 'user-tag', 'juniors'],
  },
  {
    id: 'user-tag', section: 'people', parent: 'profile', name: 'User tag', art: 'tag',
    line: 'A unique handle so friends can find you.',
    aliases: ['handle', 'username', '@', 'find friends', 'people search'],
    what: 'Every member gets a unique tag (lower case letters, digits, dots and underscores). A name search can never identify a stranger; an exact tag can.',
    holds: ['The tag, shown on your profile'],
    sees: 'Anyone who types it exactly', manages: 'The person',
    rules: ['3 to 20 characters, unique; a deleted account frees its tag.', 'Club mates and accepted follows are found by name as well.'],
    related: ['follows', 'profile-privacy'],
  },
  {
    id: 'consents', section: 'people', parent: 'account', name: 'Consents & accepted terms', art: 'document',
    line: 'What you agreed to, and which edition.',
    aliases: ['terms', 'privacy policy', 'cookies', 'telemetry', 'legal'],
    what: 'A record of every agreement: Tap Leagues terms and their edition, club and meet terms, membership terms, crash reports and usage analytics.',
    holds: ['Consent key, granted or not, the wording shown, the edition'],
    sees: 'The person', manages: 'The person',
    rules: ['A new edition of the terms asks again before you carry on.'],
    related: ['payment-terms'],
  },
  {
    id: 'combine-accounts', section: 'people', parent: 'sign-in-methods', name: 'Combine accounts', art: 'merge',
    line: 'Two accounts for one person become one.',
    aliases: ['merge', 'duplicate account', 'account merge', 'player merge'],
    what: 'Someone who registered by email and later signed in with Apple or Google can combine the two, proving they own both with a password or a social sign-in. Tap Leagues can merge the rest on request.',
    holds: ['A gated Home card when a duplicate is likely', 'Self-service combine', 'Merges done by Tap Leagues on request'],
    sees: 'The person', manages: 'The person; Tap Leagues on request',
    rules: ['Results, clubs and memberships move to the kept account.'],
    related: ['sign-in-methods'],
  },
  {
    id: 'sign-up', section: 'people', parent: 'account', name: 'Signing up & verifying', art: 'door',
    line: 'Register, verify your email, fill in your details.',
    aliases: ['register', 'registration', 'verify email', 'email verification', 'password reset', 'forgot password', 'details required', 'create account'],
    what: 'Register with an email and password or with Apple or Google, verify the email, and complete your details before you carry on. An invite link signs you up and joins you in one go.',
    holds: ['Registration and the email verification link', 'The details step: name, date of birth, user tag', 'Forgotten password: a reset link by email', 'Sign in or sign up straight from an invite link'],
    sees: 'The person', manages: 'The person',
    rules: ['Signing in with Apple or Google never creates a second account by surprise: you choose to link or create.', 'A new edition of the terms is accepted on the way in.'],
    related: ['sign-in-methods', 'user-tag', 'join-link', 'consents'],
  },
  {
    id: 'mobile-app', section: 'people', parent: 'account', name: 'The app on your phone', art: 'phone',
    line: 'iPhone and Android, with push notifications.',
    aliases: ['mobile app', 'ios', 'android', 'app store', 'play store', 'push', 'update the app', 'web view'],
    what: 'Everything you do as a player happens in the app as well as on the website: competitions, challenges, scores, chats, RSVPs, bookings and payments.',
    holds: ['Push notifications per device', 'An update prompt when a version is too old', 'Help pages opened inside the app'],
    sees: 'The person', manages: 'The person',
    rules: ['Admin work is best done on the website; the app covers the day-to-day.'],
    related: ['notifications', 'notification-preferences'],
  },
  {
    id: 'profile', section: 'people', parent: 'players', name: 'Player profile', art: 'person',
    line: 'Name, photo, playing category, region, stats.',
    aliases: ['your profile', 'avatar', 'date of birth', 'playing category'],
    what: 'What other people see of a player, and the place their stats live.',
    holds: ['Name, photo, playing category, region', 'Date of birth (age rules for memberships)', 'Stats per club and competition', 'Squash Levels link'],
    sees: 'Per profile privacy', manages: 'The person; admins for managed players',
    rules: ['Leaderboard opt-in with a chosen leaderboard name.'],
    related: ['player-stats', 'profile-privacy', 'squash-levels'],
  },
  {
    id: 'player-stats', section: 'people', parent: 'profile', name: 'Player stats', art: 'chart',
    line: 'Wins, losses, streaks, points, per club and competition.',
    aliases: ['statistics', 'win percentage', 'streak', 'leaderboard'],
    what: 'Built from approved matches only: wins, draws, losses, points for and against, streaks, average ladder position.',
    holds: ['Per club, per competition, per meet', 'Club and competition leaderboards'],
    sees: 'Per profile privacy', manages: 'Worked out from approved matches',
    rules: ['Only fully approved matches count (played, both sides approved).'],
    related: ['stats', 'result-approval'],
  },
  {
    id: 'squash-levels', section: 'people', parent: 'profile', name: 'Squash Levels', art: 'chart',
    line: 'Results sent to SquashLevels.com.',
    aliases: ['external rating', 'levels'],
    what: 'Squash clubs can send approved results to Squash Levels; each player chooses whether theirs go.',
    holds: ['Per-player opt-in', 'Per-competition switch', 'The player\'s current level'],
    sees: 'The person', manages: 'The person; club admins per competition',
    rules: ['Results are sent in Squash Levels\' own format.', 'Never used for ranking or balancing inside Tap Leagues.'],
    related: ['sports'],
  },
  {
    id: 'managed-players', section: 'people', parent: 'players', name: 'Players without an account', art: 'person',
    line: 'Added by name; claimed later with a link.',
    aliases: ['managed player', 'no email', 'claim link', 'admin-managed'],
    what: 'An admin adds a player by name. They appear in every league, ladder and draw like anyone else, and can claim the profile later.',
    holds: ['Name, optional email and phone', 'Junior flag', 'A claim link that turns the profile into an account'],
    sees: 'Everyone in the club', manages: 'Club admins',
    rules: ['Entry fees for a managed player sit dormant until the profile is claimed.', 'Admins answer follow requests for them.'],
    related: ['players-admin', 'follows', 'juniors'],
  },
  {
    id: 'juniors', section: 'people', parent: 'players', name: 'Junior protections', art: 'shield',
    line: 'Under-18s are never public.',
    aliases: ['under 18', 'child', 'minor', 'parent'],
    what: 'A junior flag on a player applies the protections from day one.',
    holds: ['Never on public pages, widgets or leaderboards', 'Cannot be followed', 'No AI previews or match reports involving them', 'Parent or carer consent for membership applications', 'Health answers reviewed by the parent for under-16s'],
    sees: 'Everyone (the protection), club admins (the flag)', manages: 'Club admins',
    rules: ['Initials only to anyone outside their club; "First L." on Clubhouse TV.', 'The junior flag can be set by an admin or comes from the date of birth.'],
    related: ['parent-consent', 'profile-privacy'],
  },
  {
    id: 'follows', section: 'people', parent: 'players', name: 'Following', art: 'link',
    line: 'Mutual, consent-based links between two adults.',
    aliases: ['follow request', 'friends', 'find friends', 'followers'],
    what: 'Follow somebody in another club by their user tag; they accept or decline, and then you see each other\'s results.',
    holds: ['Requests: pending, accepted, declined', 'Admin approvals for managed players'],
    sees: 'The two people', manages: 'The two people; club admins for managed players',
    rules: ['Adults only.'],
    related: ['user-tag', 'follow-approvals'],
  },
  {
    id: 'blocking', section: 'people', parent: 'follows', name: 'Blocking', art: 'shield',
    line: 'Blocked people cannot message you.',
    aliases: ['block user', 'safety'],
    what: 'Block somebody and they cannot start or continue a chat with you.',
    holds: ['Who blocked whom'],
    sees: 'The person who blocked', manages: 'The person',
    rules: ['Shared competition chats still show both, but direct messages stop.'],
    related: ['chats', 'message-reports'],
  },

  // ── Box leagues ────────────────────────────────────────────────────────
  {
    id: 'league', section: 'leagues', parent: null, name: 'Box league', art: 'table',
    line: 'Divisions of similar strength that roll over.',
    aliases: ['league', 'box', 'boxes', 'season', 'round robin'],
    what: 'The classic club league. Everyone is grouped into small divisions, plays everyone in their box over a cycle, and moves up or down when the cycle rolls over.',
    holds: ['Settings: sport, players per team, scoring system, bonus points, rollover change, match length', 'Entry fee per team per cycle', 'Handicaps', 'League cycles', 'Invite settings and a chat'],
    sees: 'Per the competition\'s visibility', manages: 'Club admins',
    rules: ['The entry fee is set once on the league and applies to each new cycle.', 'Information text shows on the page for players.'],
    related: ['league-cycle', 'entry-fees', 'handicaps', 'scoring-systems', 'join-link'],
  },
  {
    id: 'league-cycle', section: 'leagues', parent: 'league', name: 'League cycles', art: 'calendar',
    line: 'A season with a start and end that rolls into the next.',
    aliases: ['cycle', 'season', 'round', 'rollover'],
    what: 'One cycle of the league: divisions laid out, matches to play, a table to climb. Published when ready; the next cycle is built from this one.',
    holds: ['Start and end dates, published or draft', 'Divisions', 'Its own scoring system and bonus points', 'Waiting area, invite link and chat', 'A link to the cycle it rolled into'],
    sees: 'Per the competition\'s visibility', manages: 'Club admins',
    rules: ['The previous cycle is kept whole for the record.'],
    related: ['league-division', 'cycle-wizard', 'rollover', 'waiting-area'],
  },
  {
    id: 'cycle-wizard', section: 'leagues', parent: 'league-cycle', name: 'Cycle wizard', art: 'wand', admin: true,
    line: 'Tick who is playing; divisions are laid out.',
    aliases: ['create cycle', 'wizard', 'new season'],
    what: 'Builds a cycle in minutes: pick the players (anyone who joined by link is waiting), set divisions and dates, publish.',
    holds: ['Player picks and division layout', 'Dates and bonus points'],
    sees: 'Club admins', manages: 'Club admins',
    rules: ['An invite link can land a joiner straight into the wizard flow.'],
    related: ['league-cycle', 'waiting-area'],
  },
  {
    id: 'rollover', section: 'leagues', parent: 'league-cycle', name: 'Rollover', art: 'swap', admin: true,
    line: 'Promotion, relegation, dropouts, in one guided flow.',
    aliases: ['promotion', 'relegation', 'next cycle'],
    what: 'Previews the next cycle from this one: who moves up, who moves down, who dropped out, who is waiting, then applies it.',
    holds: ['Default movement per league', 'Preview before applying'],
    sees: 'Club admins', manages: 'Club admins',
    rules: ['Finish top and move up a box; finish bottom and drop down.'],
    related: ['league-cycle', 'league-standings'],
  },
  {
    id: 'next-cycle', section: 'leagues', parent: 'rollover', name: 'Next cycle', art: 'calendar', admin: true,
    line: 'The cycle the rollover builds.',
    aliases: ['new cycle', 'new season', 'following cycle'],
    what: 'The rollover creates the next cycle from this one: the same divisions with everyone moved up, down or out, a new waiting area, and the dates you set. It starts as a draft you publish when ready.',
    holds: ['Divisions carried over with the movements applied', 'Dropouts left out; the waiting area carried across', 'Its own dates, invite link and chat'],
    sees: 'Per the competition\'s visibility once published', manages: 'Club admins',
    rules: ['A box league runs one cycle after another; each cycle has its own divisions, matches and table.', 'The previous cycle is kept whole for the record.'],
    related: ['rollover', 'league-cycle', 'league-division'],
  },
  {
    id: 'league-division', section: 'leagues', parent: 'league-cycle', name: 'Divisions', art: 'grid',
    line: 'A box of similar strength.',
    aliases: ['box', 'group', 'division'],
    what: 'A small group of teams who all play each other once in the cycle.',
    holds: ['Teams in order', 'Matches, one per pair', 'Standings', 'A division chat'],
    sees: 'Per the competition\'s visibility', manages: 'Club admins',
    rules: ['Unlimited divisions and players per division.'],
    related: ['league-team', 'league-matches', 'league-standings', 'chats'],
  },
  {
    id: 'league-team', section: 'leagues', parent: 'league-division', name: 'Teams', art: 'people',
    line: 'A singles entry or a doubles pair.',
    aliases: ['team', 'entry', 'pair', 'doubles'],
    what: 'An entry in the box: one player, or two for doubles. Carries its handicap and order.',
    holds: ['Players in the team (one or two)', 'Team name or initials', 'Handicap'],
    sees: 'Per the competition\'s visibility', manages: 'Club admins',
    rules: ['Every player in a team must qualify where a membership is required.', 'Initials are built once; a team name can be set.'],
    related: ['players', 'handicaps', 'entry-fees'],
  },
  {
    id: 'league-matches', section: 'leagues', parent: 'league-division', name: 'Matches', art: 'scoreboard',
    line: 'One per pair of teams in the box.',
    aliases: ['fixtures', 'games', 'results'],
    what: 'Each team plays each other team once. Players arrange a time, record the score, the other side or an admin approves it.',
    holds: ['Scheduled time and playing area', 'Games and result', 'Points and bonus'],
    sees: 'Per the competition\'s visibility', manages: 'The two sides; club admins',
    rules: ['Bonus points for completing all your matches, if the league gives them.'],
    related: ['match', 'result-approval', 'scheduling'],
  },
  {
    id: 'league-standings', section: 'leagues', parent: 'league-division', name: 'Standings', art: 'table',
    line: 'Points build the table.',
    aliases: ['table', 'league table', 'positions'],
    what: 'The division table: points first, then the scoring system\'s tie-breaks.',
    holds: ['Played, won, lost, points, bonus'],
    sees: 'Per the competition\'s visibility', manages: 'Worked out from results',
    rules: ['Points are primary everywhere.', 'Printable tables and PDF export.'],
    related: ['scoring-systems', 'widgets-competition', 'rollover'],
  },

  // ── Ladders ────────────────────────────────────────────────────────────
  {
    id: 'ladder', section: 'ladders', parent: null, name: 'Ladder', art: 'ladder',
    line: 'One ranked list. Challenge above you.',
    aliases: ['ranking', 'pyramid', 'challenge ladder'],
    what: 'Everyone ranked top to bottom. Challenge someone within range above you, beat them, take their place.',
    holds: ['Rules: challenge range above and below, expiry days, unrestricted challenges, concurrent challenges, win movement, decline penalty, top-players protection', 'Positions', 'Challenges', 'Inactivity drops', 'Entry fee, handicaps, invite settings, chat'],
    sees: 'Per the competition\'s visibility', manages: 'Club admins',
    rules: ['Win movement: winner goes above (everyone between moves down one) or the two swap.', 'Range below 0 means nobody challenges down; range above is at least 1.', 'A ladder may allow a player in more than one team.'],
    related: ['ladder-positions', 'challenge', 'inactivity', 'ladder-rules'],
  },
  {
    id: 'ladder-rules', section: 'ladders', parent: 'ladder', name: 'Rules', art: 'gauge',
    line: 'Range, expiry, movement, penalties.',
    aliases: ['challenge range', 'expiry', 'win movement', 'swap places', 'rejection rule'],
    what: 'The settings that make each ladder its own competition.',
    holds: ['How far above and below you can challenge', 'Days before a challenge expires', 'Unrestricted challenges for the top few', 'Winner goes above or swap places', 'Declining a challenge: allowed, counts as a loss, or drops you a set number of places', 'Hold joiners for placement'],
    sees: 'Everyone on the ladder (rules card)', manages: 'Club admins',
    rules: ['Challenges expire if ignored, so there is no hiding at the top.'],
    related: ['ladder', 'challenge'],
  },
  {
    id: 'ladder-positions', section: 'ladders', parent: 'ladder', name: 'Positions', art: 'ladder',
    line: 'Where each player or team stands.',
    aliases: ['rung', 'rank', 'standing', 'team on ladder'],
    what: 'One rung per entrant, with everything that moved it.',
    holds: ['Current position', 'Position changes with a reason', 'Handicap', 'Frozen (paused) with a reason', 'Inactivity clock and play-by date'],
    sees: 'Per the competition\'s visibility', manages: 'Club admins (move, add, remove, freeze)',
    rules: ['New joiners can be placed from the waiting area or added in ranked order.'],
    related: ['position-changes', 'freeze', 'inactivity', 'waiting-area'],
  },
  {
    id: 'position-changes', section: 'ladders', parent: 'ladder-positions', name: 'Position changes', art: 'swap',
    line: 'Every move and why.',
    aliases: ['history', 'moved down', 'moved up'],
    what: 'Old position, new position and the reason: a win, a decline or expiry forfeit, an inactivity drop, or an admin move.',
    holds: ['Old and new position', 'Reason and the match, when there was one'],
    sees: 'Per the competition\'s visibility', manages: 'Kept automatically',
    rules: ['Joint drops happen together so they never undo each other.'],
    related: ['challenge-match', 'inactivity'],
  },
  {
    id: 'freeze', section: 'ladders', parent: 'ladder-positions', name: 'Freeze', art: 'pause',
    line: 'Pause a position: away or injured.',
    aliases: ['frozen', 'pause', 'holiday', 'injury'],
    what: 'A frozen entrant cannot be challenged and cannot challenge. The inactivity countdown pauses too.',
    holds: ['Frozen since, reason'],
    sees: 'Everyone on the ladder', manages: 'The player (unfreeze) and club admins',
    rules: ['Frozen time is added back to the countdown, so a freeze never costs you days.'],
    related: ['inactivity', 'ladder-positions'],
  },
  {
    id: 'inactivity', section: 'ladders', parent: 'ladder', name: 'Inactivity drops', art: 'timer',
    line: 'Play within a set number of days or drop down.',
    aliases: ['inactivity demotion', 'play by', 'inactive'],
    what: 'An optional rule that keeps the ladder alive: a daily sweep warns, then drops anyone who has not played in the window.',
    holds: ['Days allowed and places dropped', 'A warning before the drop', 'Play-by date on the ladder'],
    sees: 'Everyone on the ladder', manages: 'Club admins',
    rules: ['The window starts at the latest of: last approved match, rule switched on, joined, last drop.', 'The top position is exempt; a freeze pauses the clock.'],
    related: ['ladder-positions', 'freeze', 'notifications'],
  },
  {
    id: 'challenge', section: 'ladders', parent: 'ladder', name: 'Challenges', art: 'swords',
    line: 'Challenge above you; they accept, you play.',
    aliases: ['challenge', 'accept', 'decline', 'suggested time'],
    what: 'A challenge from one entrant to another within range, with a suggested time and place, a message and a chat. Accept creates the match.',
    holds: ['Challenger and challenged', 'Suggested time and playing area (changeable while pending)', 'Accepted, declined or expired', 'A chat for the two sides', 'An AI preview of the chance of success'],
    sees: 'Both sides and admins (the ladder lists them)', manages: 'The two sides; club admins',
    rules: ['Only Accept accepts: nothing else can turn a challenge into a match.', 'Accept options: agree the time, suggest another, or decide later.', 'The match is created on accept, never before.', 'Expiry reminders at 24 hours and 1 hour.'],
    related: ['challenge-match', 'challenge-chat', 'scheduling', 'ladder-rules'],
  },
  {
    id: 'challenge-accept', section: 'ladders', parent: 'challenge', name: 'Accepting a challenge', art: 'check',
    line: 'Agree the time, suggest another, or decide later.',
    aliases: ['accept', 'decline', 'counter', 'decide later', 'when and where'],
    what: 'The challenged player answers: accept as suggested, accept with another time and place, or accept and settle the time later. Declining, or letting it expire, follows the ladder\'s rules.',
    holds: ['Accept: agreed, counter-suggestion or decide later', 'Decline, with its consequence from the ladder rules', 'Expiry reminders at 24 hours and 1 hour'],
    sees: 'Both sides', manages: 'The challenged player',
    rules: ['Only Accept creates the match; nothing else can.', 'A declined or expired challenge can move the decliner down or forfeit the match, as the ladder rules say.'],
    related: ['challenge', 'challenge-match', 'ladder-rules'],
  },
  {
    id: 'challenge-match', section: 'ladders', parent: 'challenge', name: 'Match', art: 'scoreboard',
    line: 'Made on accept; the result moves positions.',
    aliases: ['challenge match', 'result'],
    what: 'The match a challenge becomes. Its approved result moves the winner per the ladder\'s win movement.',
    holds: ['Time and place, games, result, handicap start'],
    sees: 'Per the competition\'s visibility', manages: 'The two sides; club admins',
    rules: ['A decline or expiry forfeit also counts as a win for movement when the rules say so.', 'Admin-only cancel once a match is arranged; nobody cancels once there is a result.'],
    related: ['match', 'position-changes'],
  },
  {
    id: 'challenge-chat', section: 'ladders', parent: 'challenge', name: 'Challenge chat', art: 'chat',
    line: 'Sort out the details between you.',
    aliases: ['message', 'chat'],
    what: 'A chat for the two sides of a challenge, opened from the challenge.',
    holds: ['Messages, read state'],
    sees: 'The two sides', manages: 'The two sides',
    rules: ['Same chat rules as everywhere else: report, delete your own, blocks respected.'],
    related: ['chats'],
  },

  // ── Tournaments ────────────────────────────────────────────────────────
  {
    id: 'tournament', section: 'tournaments', parent: null, name: 'Tournament', art: 'trophy',
    line: 'Knockout, or groups then knockout.',
    aliases: ['knockout', 'cup', 'championship', 'draw', 'finals day'],
    what: 'The big occasion: a straight bracket, or round-robin groups feeding a finals bracket, with seeding and a champion.',
    holds: ['Format, seeding strategy, third-place playoff, group and advance counts', 'Entries', 'Stages', 'Entry fee, handicaps, invite settings, chat'],
    sees: 'Per the competition\'s visibility', manages: 'Club admins',
    rules: ['Knockouts never end in a draw.', 'Handicaps can re-rate at the end of each stage.'],
    related: ['tournament-entries', 'tournament-stage', 'draw'],
  },
  {
    id: 'tournament-entries', section: 'tournaments', parent: 'tournament', name: 'Entries', art: 'people',
    line: 'Teams in the draw, with seeds.',
    aliases: ['teams', 'seeding', 'seeds', 'entrants'],
    what: 'Who is in: singles or doubles entries, each with an optional seed and handicap.',
    holds: ['Team and its players', 'Seed, handicap, name'],
    sees: 'Per the competition\'s visibility', manages: 'Club admins',
    rules: ['Entry by link goes through invite settings like any competition.'],
    related: ['draw', 'entry-fees', 'join-link'],
  },
  {
    id: 'tournament-stage', section: 'tournaments', parent: 'tournament', name: 'Stages', art: 'bracket',
    line: 'Group stage, knockout rounds, third-place playoff.',
    aliases: ['round', 'quarter final', 'semi final', 'final', 'group stage'],
    what: 'Each stage holds the teams in it and their matches. Group stages have tables; knockout stages have a bracket.',
    holds: ['Type: group, knockout round, third-place playoff', 'Teams in the stage (byes, bracket position, group finish)', 'Matches'],
    sees: 'Per the competition\'s visibility', manages: 'Club admins',
    rules: ['Results progress the bracket automatically.'],
    related: ['tournament-groups', 'bracket', 'stage-matches'],
  },
  {
    id: 'tournament-groups', section: 'tournaments', parent: 'tournament-stage', name: 'Groups', art: 'grid',
    line: 'Round-robin tables that decide who goes through.',
    aliases: ['group table', 'pool'],
    what: 'Small round-robin groups; the top few advance to the knockout.',
    holds: ['Group table', 'Advance count'],
    sees: 'Per the competition\'s visibility', manages: 'Club admins',
    rules: ['Group winners progress when the admin says so.'],
    related: ['bracket', 'scoring-systems'],
  },
  {
    id: 'bracket', section: 'tournaments', parent: 'tournament-stage', name: 'Bracket', art: 'bracket',
    line: 'Win or go home.',
    aliases: ['draw', 'tree', 'knockout bracket'],
    what: 'The knockout tree: positions, byes, who advanced from where.',
    holds: ['Bracket positions and byes', 'Progression between rounds'],
    sees: 'Per the competition\'s visibility', manages: 'Club admins',
    rules: ['The bracket runs live as results come in; followable on a phone and on the website widget.'],
    related: ['widgets-competition', 'tv-display'],
  },
  {
    id: 'stage-matches', section: 'tournaments', parent: 'tournament-stage', name: 'Matches', art: 'scoreboard',
    line: 'Each round\'s matches.',
    aliases: ['fixtures', 'ties'],
    what: 'Matches in a stage, scheduled and scored like any match.',
    holds: ['Time and place, games, result, handicap start'],
    sees: 'Per the competition\'s visibility', manages: 'The two sides; club admins',
    rules: ['A winner must be decided in a knockout.'],
    related: ['match'],
  },
  {
    id: 'advancing', section: 'tournaments', parent: 'tournament-stage', name: 'Going through', art: 'swap',
    line: 'Group finishers and round winners move on.',
    aliases: ['advance', 'progress', 'qualify', 'next round', 'knocked out'],
    what: 'When a stage\'s matches are done, the teams that go through are placed in the next stage: the top of each group into the bracket by finishing position, each round\'s winners into the next round.',
    holds: ['How many go through from each group', 'Bracket positions filled as results come in', 'Who is out'],
    sees: 'Per the competition\'s visibility', manages: 'Automatic from results; club admins can adjust',
    rules: ['Results progress the bracket automatically.', 'A bye goes straight through.'],
    related: ['tournament-groups', 'bracket', 'stage-matches', 'draw'],
  },
  {
    id: 'draw', section: 'tournaments', parent: 'tournament', name: 'Draw generation', art: 'wand', admin: true,
    line: 'One click builds or rebuilds the bracket.',
    aliases: ['generate draw', 'seeding strategy', 'rebuild'],
    what: 'Builds stages, groups and the bracket from the entries and the seeding strategy.',
    holds: ['Seeding: random, manual or by group finishing position', 'Group count and advance count'],
    sees: 'Club admins', manages: 'Club admins',
    rules: ['Rebuilding replaces an unplayed draw.'],
    related: ['tournament-stage', 'tournament-entries'],
  },

  // ── Americano leagues ──────────────────────────────────────────────────
  {
    id: 'americano', section: 'americano', parent: null, name: 'Americano league', art: 'swap',
    line: 'Enter solo, partner everyone once.',
    aliases: ['americano', 'mexicano', 'whist', 'social doubles'],
    what: 'Doubles with a twist: you enter on your own, partner every player in your division once, and every result counts towards your personal standing.',
    holds: ['Default division size (4, 8 or 12) and rollover change', 'Cycles', 'Entry fee per player per cycle, handicaps, invite settings, chat'],
    sees: 'Per the competition\'s visibility', manages: 'Club admins',
    rules: ['4 players is 3 matches, 8 is 14, 12 is 33: the whole schedule exists when the cycle starts.'],
    related: ['americano-cycle', 'americano-division'],
  },
  {
    id: 'americano-cycle', section: 'americano', parent: 'americano', name: 'Cycles', art: 'calendar',
    line: 'A block of weeks; players roll over individually.',
    aliases: ['season', 'round', 'rollover'],
    what: 'One cycle with its divisions and seats. Promotion and relegation happen per player at rollover.',
    holds: ['Dates, published or draft', 'Divisions', 'Players in the cycle (seats)', 'Waiting area, invite link, chat'],
    sees: 'Per the competition\'s visibility', manages: 'Club admins',
    rules: ['An invite link can hand new players to the cycle wizard.'],
    related: ['americano-division', 'americano-wizard', 'waiting-area'],
  },
  {
    id: 'americano-wizard', section: 'americano', parent: 'americano-cycle', name: 'Cycle wizard', art: 'wand', admin: true,
    line: 'Pick who is playing; every match is generated.',
    aliases: ['new division', 'create cycle'],
    what: 'Picks players into divisions of 4, 8 or 12 and generates the whist rotation instantly. A new division can be added to a running cycle.',
    holds: ['Division sizes', 'Seat order'],
    sees: 'Club admins', manages: 'Club admins',
    rules: ['Sizes are fixed so the rotation works out perfectly.'],
    related: ['americano-division', 'americano-seats'],
  },
  {
    id: 'americano-division', section: 'americano', parent: 'americano-cycle', name: 'Divisions', art: 'grid',
    line: '4, 8 or 12 seats at your level.',
    aliases: ['group', 'box'],
    what: 'A fixed-size group whose matches are all generated up front.',
    holds: ['Seats', 'Rounds of matches', 'Personal standings'],
    sees: 'Per the competition\'s visibility', manages: 'Club admins',
    rules: ['Seats and matches always agree: editing the seats updates the matches.'],
    related: ['americano-seats', 'americano-rounds', 'americano-standings'],
  },
  {
    id: 'americano-seats', section: 'americano', parent: 'americano-division', name: 'Seats', art: 'person',
    line: 'One player per seat; a substitute takes it over.',
    aliases: ['players', 'seat', 'substitute', 'swap player', 'fill empty seat'],
    what: 'Each player holds a seat in the division. A mid-cycle substitute takes over the seat and its remaining matches; results already played stay.',
    holds: ['Player, seat order, handicap, display name'],
    sees: 'Per the competition\'s visibility', manages: 'Club admins',
    rules: ['Seats are withdrawn when a player leaves or is removed; an empty seat can be filled.'],
    related: ['waiting-area', 'players'],
  },
  {
    id: 'americano-rounds', section: 'americano', parent: 'americano-division', name: 'Rounds', art: 'calendar',
    line: 'A whist rotation: new partner every round.',
    aliases: ['rotation', 'round robin', 'pairings'],
    what: 'Each round pairs everyone so you partner every player once and face them twice.',
    holds: ['Matches in the round'],
    sees: 'Per the competition\'s visibility', manages: 'Generated automatically',
    rules: ['With handicaps on, each pair plays off the option nearest the mean of the two players (bowls sums).'],
    related: ['americano-matches', 'handicaps'],
  },
  {
    id: 'americano-matches', section: 'americano', parent: 'americano-rounds', name: 'Matches', art: 'scoreboard',
    line: 'Doubles matches, personal points.',
    aliases: ['fixtures', 'games'],
    what: 'Each match gives its points to each of the four players.',
    holds: ['Time and place, games, result'],
    sees: 'Per the competition\'s visibility', manages: 'The players; club admins',
    rules: ['Every result counts towards your own standing, whoever your partner was.'],
    related: ['match', 'americano-standings'],
  },
  {
    id: 'americano-standings', section: 'americano', parent: 'americano-division', name: 'Personal standings', art: 'table',
    line: 'Ranked on match points; sets, then games break ties.',
    aliases: ['table', 'standings'],
    what: 'The division table per player.',
    holds: ['Points, sets, games'],
    sees: 'Per the competition\'s visibility', manages: 'Worked out from results',
    rules: ['Points first; ties break on sets, then games.'],
    related: ['widgets-competition'],
  },

  // ── Meets ──────────────────────────────────────────────────────────────
  {
    id: 'meet', section: 'meets', parent: null, name: 'Meet', art: 'handshake',
    line: 'A recurring club night. RSVP per date.',
    aliases: ['social', 'club night', 'session', 'mixer', 'event', 'social play'],
    what: 'The Wednesday mixer, the Friday social: a recurring (or one-off) session players RSVP to date by date. Matches are made on the night; paid sessions bill themselves.',
    holds: ['Schedule: weekly, custom dates or one-off; duration', 'Capacity, minimum, waitlist, RSVP open and close', 'Fee, billing frequency, cancellation terms, terms', 'Match generation settings and skill order', 'Meet players, dates, chat, invite settings'],
    sees: 'Per the competition\'s visibility (private meets need approval)', manages: 'Club admins',
    rules: ['A meet is one thing with its dates; there is nothing else to set up.', 'Price changes need notice; a changed price is re-offered to people already in.'],
    related: ['meet-occurrence', 'meet-players', 'cancellation-terms', 'session-fees', 'meet-price-change'],
  },
  {
    id: 'meet-players', section: 'meets', parent: 'meet', name: 'Meet players', art: 'people',
    line: 'Who has joined the meet.',
    aliases: ['members', 'regulars', 'skill order', 'skill rank'],
    what: 'Everyone who joined the meet, with the admin\'s skill order used to balance matches.',
    holds: ['Skill rank per player', 'Bill-to assignments'],
    sees: 'Meet players and admins', manages: 'Club admins',
    rules: ['Join once, then RSVP per date.'],
    related: ['bill-to', 'meet-rsvp'],
  },
  {
    id: 'meet-occurrence', section: 'meets', parent: 'meet', name: 'Dates', art: 'calendar-check',
    line: 'One session: RSVPs, matches, charges.',
    aliases: ['occurrence', 'session', 'night', 'date'],
    what: 'A single date of the meet with its own register, generated matches and, when paid, its charges.',
    holds: ['Start, end, status (open, cancelled, rescheduled, finalised)', 'Fee and capacity overrides', 'RSVPs and the waitlist', 'Matches on the night', 'Held playing areas'],
    sees: 'Meet players and admins', manages: 'Club admins',
    rules: ['RSVPs open a set number of days before, with a notification.', 'Under the minimum: a heads-up a day before RSVPs close.', 'Deleting a meet removes its unplayed matches on every date.'],
    related: ['meet-rsvp', 'meet-matches', 'register', 'session-fees'],
  },
  {
    id: 'meet-rsvp', section: 'meets', parent: 'meet-occurrence', name: 'RSVPs', art: 'calendar-check',
    line: 'In, waitlisted, offered, cancelled, no-show.',
    aliases: ['attendance', 'register', 'count me in', 'walk-in'],
    what: 'Each person\'s answer for a date, with the terms and price they agreed to.',
    holds: ['Status, waitlist position, added on the night', 'Agreed price, terms version and cancellation tiers', 'Cancellation charge and waiver'],
    sees: 'The person and admins; the register shows who is in', manages: 'The person; club admins',
    rules: ['First come, first served.', 'A paid date needs a saved card before you can RSVP.', 'Your confirmation email is the record and never switches off.'],
    related: ['waitlist', 'session-fees', 'saved-cards'],
  },
  {
    id: 'waitlist', section: 'meets', parent: 'meet-rsvp', name: 'Waitlist', art: 'hourglass',
    line: 'A freed spot is offered onward.',
    aliases: ['waiting list', 'offer', 'promotion'],
    what: 'When a date is full, the next person waits; when a spot opens it is offered to them with a set time to accept.',
    holds: ['Position, offer, offer window'],
    sees: 'The person and admins', manages: 'Automatic; admins can add or remove',
    rules: ['Late cancellation fees are provisional: the spot is offered onward immediately and the fee only sticks if nobody took it.'],
    related: ['meet-rsvp', 'cancellation-terms'],
  },
  {
    id: 'meet-matches', section: 'meets', parent: 'meet-occurrence', name: 'Matches on the night', art: 'scoreboard',
    line: 'Generated so everyone plays.',
    aliases: ['fixtures', 'pairings', 'rounds', 'generate matches'],
    what: 'Balanced matches made from who is in, in rounds, avoiding recent repeats. Results count towards stats.',
    holds: ['Rounds and matches per date', 'Team size (singles or doubles)'],
    sees: 'Meet players and admins', manages: 'Club admins (generate, regenerate, delete any meet match)',
    rules: ['Generation can trigger when the register fills.', 'Results are recorded like any match.'],
    related: ['match', 'register'],
  },
  {
    id: 'register', section: 'meets', parent: 'meet-occurrence', name: 'Event-day register', art: 'clipboard', admin: true,
    line: 'Arrivals, matches, results, finalise.',
    aliases: ['console', 'check in', 'finalise', 'event day'],
    what: 'One screen to run the night: mark arrivals, add people who turned up unannounced, generate matches, record results, finalise the session so billing runs.',
    holds: ['Arrivals and no-shows', 'People who turned up unannounced', 'Finalise'],
    sees: 'Club admins', manages: 'Club admins',
    rules: ['A paid date that ended without its charges running reminds admins to review the register.'],
    related: ['meet-matches', 'session-fees'],
  },
  {
    id: 'cancellation-terms', section: 'meets', parent: 'meet', name: 'Cancellation terms', art: 'document',
    line: 'What a late cancel costs, by hours before.',
    aliases: ['cancellation tiers', 'late cancel', 'grace window', 'no-show'],
    what: 'Tiers of charge by how long before the start somebody cancels, a grace window after RSVP, and no-shows paying in full.',
    holds: ['Tiers: hours before and percent charged', 'Grace window hours', 'Terms text and version'],
    sees: 'Everyone on the meet before they RSVP', manages: 'Club admins',
    rules: ['Follows consumer cancellation rules: what you agreed to is stored with your RSVP.'],
    related: ['waitlist', 'payment-terms'],
  },
  {
    id: 'bill-to', section: 'meets', parent: 'meet', name: 'Pay for someone else', art: 'receipt',
    line: 'A parent pays for a child; a partner pays for both.',
    aliases: ['bill to', 'billing assignment', 'payer'],
    what: 'A meet player asks another account to pay their session fees; the payer accepts and is charged instead.',
    holds: ['Payer, status, accepted terms version'],
    sees: 'The two people and admins', manages: 'The two people',
    rules: ['The payer sees the same terms the player agreed to.'],
    related: ['session-fees', 'payment-request'],
  },
  {
    id: 'meet-price-change', section: 'meets', parent: 'meet', name: 'Price changes', art: 'coin',
    line: 'A new price needs notice; people already in are asked again.',
    aliases: ['change price', 'price notice', 're-offer', 'new price'],
    what: 'A paid meet can change its price. Dates inside the notice window keep the old price; everyone already in for later dates is asked to accept the new one.',
    holds: ['The new price and when it starts', 'Who has accepted it'],
    sees: 'Meet players', manages: 'Club admins',
    rules: ['Nobody is charged a price they did not agree to.'],
    related: ['session-fees', 'meet-rsvp', 'payment-terms'],
  },

  // ── Matches & scoring ──────────────────────────────────────────────────
  {
    id: 'match', section: 'matches', parent: null, name: 'Match', art: 'scoreboard',
    line: 'Two sides, a time and place, games, a result.',
    aliases: ['fixture', 'game', 'tie', 'result'],
    what: 'The one record every format produces: a league match, a challenge match, a bracket tie, an americano doubles match or a match on a meet night.',
    holds: ['Home and away sides', 'Scheduled time, playing area, duration, played elsewhere', 'Games and scores', 'Result, points, bonus, handicap start', 'Approval by each side', 'Match events and live scoring'],
    sees: 'Per the competition\'s visibility', manages: 'The two sides; club admins',
    rules: ['Feeds and stats count only fully approved matches.', 'A booking of the playing area can be made from scheduling.'],
    related: ['scheduling', 'games', 'result-approval', 'live-scoring', 'playing-areas'],
  },
  {
    id: 'scheduling', section: 'matches', parent: 'match', name: 'Scheduling', art: 'calendar',
    line: 'Propose a time; the other side confirms.',
    aliases: ['schedule', 'propose date', 'availability', 'reminder', 'calendar'],
    what: 'Either side proposes a time and playing area, the other confirms (or an admin sets it). Reminders follow.',
    holds: ['Each side\'s proposed time with a reason', 'Admin-set time', 'Reminders: incomplete schedule, match approaching', 'Add to phone calendar'],
    sees: 'The two sides and admins', manages: 'The two sides; club admins',
    rules: ['Past times are refused.', 'The same minute from the other side counts as a confirm.', 'Where bookings are on, confirming a time books the playing area.'],
    related: ['match-booking', 'challenge', 'playing-areas'],
  },
  {
    id: 'games', section: 'matches', parent: 'match', name: 'Games & scores', art: 'scoreboard',
    line: 'Per-game scores that make the result.',
    aliases: ['sets', 'frames', 'legs', 'score'],
    what: 'The scores of each game, set, frame or leg, in order. The scoring system turns them into the result and points.',
    holds: ['Home and away score per game'],
    sees: 'Per the competition\'s visibility', manages: 'The two sides; club admins',
    rules: ['Partial detail is fine: a result can be entered without every game.'],
    related: ['scoring-systems', 'match-events'],
  },
  {
    id: 'match-events', section: 'matches', parent: 'games', name: 'Match events', art: 'flag',
    line: 'Goals, scorers, own goals, per sport.',
    aliases: ['scorers', 'events', 'timeline'],
    what: 'Sport-specific events inside a match, attributed to a player and side, in sequence.',
    holds: ['Event key, value, player, side, game'],
    sees: 'Per the competition\'s visibility', manages: 'The two sides; club admins',
    rules: ['A goal row must carry a side; an own goal belongs to the conceding side.'],
    related: ['games', 'live-scoring'],
  },
  {
    id: 'result-approval', section: 'matches', parent: 'match', name: 'Result approval', art: 'check',
    line: 'Entered by one side, approved by the other or an admin.',
    aliases: ['approve result', 'dispute', 'auto approve'],
    what: 'A result goes live when both sides have approved it, or when the competition auto-approves, or when an admin approves.',
    holds: ['Approved at, per side', 'Admin approval'],
    sees: 'The two sides and admins', manages: 'The two sides; club admins',
    rules: ['Only fully approved matches count for standings, feeds and stats.', 'Admins are told of results awaiting approval.'],
    related: ['player-stats', 'league-standings', 'notifications'],
  },
  {
    id: 'live-scoring', section: 'matches', parent: 'games', name: 'Live scoring', art: 'timer',
    line: 'Score point by point from a phone.',
    aliases: ['live score', 'scoreboard', 'in play'],
    what: 'A scoring session for a match: each point is an event, the engine for the scoring system keeps the state, and the Clubhouse TV shows it live.',
    holds: ['Scorer, state, events in sequence', 'Snapshot of the scoring system used'],
    sees: 'Everyone who can see the match; TV screens', manages: 'The scorer; club admins',
    rules: ['Abandoning a session leaves the match as it was.'],
    related: ['tv-display', 'scoring-systems', 'handicaps'],
  },
  {
    id: 'ai-report', section: 'matches', parent: 'result-approval', name: 'AI match report', art: 'robot',
    line: 'A short write-up of the match.',
    aliases: ['analysis', 'match report', 'preview'],
    what: 'Optional per competition: a generated report after a result, and a preview of a challenge\'s chance of success.',
    holds: ['Report text'],
    sees: 'Everyone who can see the match', manages: 'Club admins (switch it off per competition)',
    rules: ['On by default; each competition can switch it off.', 'Never for a match or challenge involving a junior.'],
    related: ['challenge'],
  },
  {
    id: 'scoring-systems', section: 'matches', parent: 'match', name: 'Scoring systems', art: 'gauge',
    line: 'How games become a result and points.',
    aliases: ['points system', 'best of', 'schema', 'outcomes'],
    what: 'Per club and sport: stock systems or custom ones. Number of games, split games, tie-break game, whether live scoring is supported, and the outcomes that give points.',
    holds: ['Outcomes & points', 'Stock or custom schema with parameters', 'Default per sport'],
    sees: 'Everyone (on the competition rules)', manages: 'Club admins',
    rules: ['A competition picks one; a league cycle can pick its own.', 'Archived systems stay on old matches.'],
    related: ['outcomes', 'league', 'ladder', 'tournament', 'americano', 'meet'],
  },
  {
    id: 'outcomes', section: 'matches', parent: 'scoring-systems', name: 'Outcomes & points', art: 'table',
    line: '3-0 is 3 points, 3-2 is 2, and so on.',
    aliases: ['points per result', 'override'],
    what: 'Each possible outcome and the points each side earns, with per-competition overrides.',
    holds: ['Outcome, home points, away points', 'Overrides'],
    sees: 'Everyone', manages: 'Club admins',
    rules: ['Points are primary in every standings table.'],
    related: ['league-standings', 'americano-standings'],
  },
  {
    id: 'handicaps', section: 'matches', parent: 'scoring-systems', name: 'Handicaps', art: 'scale',
    line: 'A fair start between players of different strength.',
    aliases: ['handicap', 'scratch', 'owe', 'receive', 'auto adjust', 'bisque', 'shots'],
    what: 'Per competition: a scheme for the sport (points start, fifteens for tennis and padel, a custom unit), how two handicaps net into a start, and optional automatic adjustment after results.',
    holds: ['Scheme, notation, direction, netting, tie-break conversion', 'Auto-adjust: off, stepped or proportional with pace presets, max change per match, min and max', 'Show to members', 'Handicap history'],
    sees: 'Everyone when shown to members', manages: 'Club admins',
    rules: ['The start is shown on every match the moment it exists.', 'Tie-breaks default to Converted; Level is opt-in.', 'Pairs play off the option nearest their mean (bowls sums).'],
    related: ['handicap-history', 'league', 'ladder', 'tournament', 'americano'],
  },
  {
    id: 'handicap-history', section: 'matches', parent: 'handicaps', name: 'Handicap history', art: 'chart',
    line: 'Every change: initial, admin, automatic, rollover, reset.',
    aliases: ['handicap change', 'adjustment'],
    what: 'Old and new value per team with the reason and the match that caused it.',
    holds: ['Old value, new value, reason, note'],
    sees: 'Everyone when handicaps are shown', manages: 'Kept automatically',
    rules: ['Stage-end timing lands all tournament moves together.'],
    related: ['handicaps'],
  },
  {
    id: 'stats', section: 'matches', parent: 'result-approval', name: 'Stats & leaderboards', art: 'chart',
    line: 'Club and competition stats from approved matches.',
    aliases: ['statistics', 'leaderboard', 'player stats', 'pdf export'],
    what: 'Competition stats pages and leaderboards, the club stats widget, PDF export of tables and draws.',
    holds: ['Per club, per competition, per meet', 'Leaderboards per format'],
    sees: 'Per the competition\'s visibility', manages: 'Worked out from results',
    rules: ['Only approved matches count.'],
    related: ['player-stats', 'widgets-stats'],
  },
  {
    id: 'team-names', section: 'matches', parent: 'match', name: 'Team names & initials', art: 'people',
    line: 'How the two sides of a match are named.',
    aliases: ['initials', 'team name', 'display name', 'doubles pair', 'partner', 'g&a'],
    what: 'A side is one player or a doubles pair. Pairs get initials made from the players ("G&A") unless the team is given a name; americano seats can carry a display name.',
    holds: ['Initials built from the players', 'A team name set by an admin', 'Display names on americano seats', 'Partner preferences when joining'],
    sees: 'Everyone who can see the match', manages: 'Club admins',
    rules: ['Private players show as "Private player" and juniors as initials, on every surface.'],
    related: ['league-team', 'tournament-entries', 'americano-seats', 'waiting-area'],
  },

  // ── Payments ───────────────────────────────────────────────────────────
  {
    id: 'payments', section: 'payments', parent: null, name: 'Club payments', art: 'terminal',
    line: 'Money goes straight to the club\'s own account.',
    aliases: ['stripe', 'money', 'fees', 'take payments', 'card'],
    what: 'A club connects its own Stripe account and takes entry fees, session fees, memberships and bookings. Tap Leagues takes a small commission plus Stripe\'s fees on each payment.',
    holds: ['Stripe account', 'Charges, refunds, receipts', 'Entry fees and session fees', 'Saved cards and wallets', 'Pay links and guest payers', 'Payment terms'],
    sees: 'Members see their own; admins see the club\'s', manages: 'Club admins; switched on by Tap Leagues',
    rules: ['Fee estimates follow the account\'s country and currency.', 'Each charge records who paid the fees.'],
    related: ['payment-account', 'charges', 'entry-fees', 'session-fees'],
  },
  {
    id: 'payment-account', section: 'payments', parent: 'payments', name: 'Stripe account', art: 'bank', admin: true,
    line: 'The club\'s own account, set up in the page.',
    aliases: ['connect', 'onboarding', 'payouts', 'requirements due', 'suspended'],
    what: 'Embedded Stripe Connect onboarding and dashboard. Status, payouts, outstanding requirements and deadlines.',
    holds: ['Status, country, currency, payouts enabled', 'Requirements currently due and their deadline', 'Suspension'],
    sees: 'Club admins', manages: 'Club admins',
    rules: ['Admins are told when payments go live, when requirements fall due, and if Stripe pauses charges.', 'Paid items cannot be published until charges are enabled.'],
    related: ['payments'],
  },
  {
    id: 'charges', section: 'payments', parent: 'payment-account', name: 'Charges', art: 'receipt',
    line: 'Each payment taken, with its receipt.',
    aliases: ['payment', 'receipt', 'transaction', 'failed payment', 'retry'],
    what: 'A charge on somebody\'s card for one or more things, with the commission and Stripe fee, its status, retries and receipt.',
    holds: ['Amount, currency, fees, who pays the fees', 'Status, failure reason, attempts, next retry', 'Line items', 'Refunds', 'Receipt and failure emails'],
    sees: 'The payer and club admins', manages: 'Club admins (refund); automatic retries',
    rules: ['A retry never charges twice.', 'A card hold shows the date it must be taken by.'],
    related: ['line-items', 'refunds', 'payments-admin', 'your-payments'],
  },
  {
    id: 'line-items', section: 'payments', parent: 'charges', name: 'What a charge covers', art: 'receipt',
    line: 'A session, an entry fee, a renewal, a booking.',
    aliases: ['line item', 'breakdown'],
    what: 'Each thing a charge paid for, so one card payment can cover several sessions.',
    holds: ['Description, amount, what it is for'],
    sees: 'The payer and club admins', manages: 'Kept automatically',
    rules: ['Meet sessions can batch weekly or monthly into one charge.'],
    related: ['session-fees', 'entry-fees', 'membership-period', 'booking'],
  },
  {
    id: 'refunds', section: 'payments', parent: 'charges', name: 'Refunds', art: 'swap',
    line: 'Full or partial, back to the card.',
    aliases: ['refund', 'money back', 'dispute', 'chargeback'],
    what: 'A refund against a charge, in full or in part.',
    holds: ['Amount, reason, status'],
    sees: 'The payer and club admins', manages: 'Club admins',
    rules: ['A full entry-fee refund puts the fee back to Due by design.', 'A club-made booking cancellation refunds in full.'],
    related: ['charges', 'entry-fees'],
  },
  {
    id: 'payouts', section: 'payments', parent: 'payment-account', name: 'Payouts', art: 'bank',
    line: 'Money from the club\'s Stripe balance to its bank.',
    aliases: ['payout', 'bank account', 'balance', 'transfer'],
    what: 'What players pay lands in the club\'s own Stripe account, less the commission and Stripe\'s fee. Stripe pays the balance out to the club\'s bank on its schedule; the embedded Stripe dashboard shows every payout.',
    holds: ['Balance and payout schedule', 'Each payout and the charges it covers', 'The bank account on the Stripe account'],
    sees: 'Club admins', manages: 'Stripe; club admins change the bank account',
    rules: ['Payouts need the Stripe account\'s requirements met; outstanding ones pause them.', 'Refunds come out of the balance, or the bank account if the balance is short.'],
    related: ['payment-account', 'charges', 'refunds'],
  },
  {
    id: 'entry-fees', section: 'payments', parent: 'charges', name: 'Entry fees', art: 'ticket',
    line: 'A fee per team to enter a competition.',
    aliases: ['entry fee', 'fees due', 'waive', 'pay now', 'obligation'],
    what: 'Leagues (per cycle), ladders, tournaments and americano leagues can charge to enter. Each team owes a fee that they pay when they like; it never blocks play.',
    holds: ['Fee due per team: Due, Paid or Waived', 'The charge once paid, a pay link, who it is assigned to', 'Terms version agreed'],
    sees: 'The team and club admins', manages: 'Club admins (waive, charge existing members)',
    rules: ['Charged after accept or add, not on join.', 'Leagues charge per cycle; the fee is set once on the league.', 'Unpaid fees show as a banner on the competition and on Your payments.'],
    related: ['league', 'ladder', 'tournament', 'americano', 'charges', 'competition-rules'],
  },
  {
    id: 'session-fees', section: 'payments', parent: 'charges', name: 'Meet session fees', art: 'coin',
    line: 'Billed after the session, never on sign-up.',
    aliases: ['session fee', 'pay per session', 'billing frequency', 'late cancellation fee'],
    what: 'Paid meets charge the saved card after each date finalises, or batch weekly or monthly. Cancellation terms decide what a late cancel or no-show pays.',
    holds: ['Billing frequency and day', 'Per-date fee overrides', 'Late cancellation charges and waivers'],
    sees: 'The attendee and club admins', manages: 'Automatic; admins review, waive and refund',
    rules: ['A saved card is needed before RSVP.', 'Refill before charge: a late-cancelled spot taken by someone else costs nothing.', 'Retries on failure with an email to update the card.'],
    related: ['meet-occurrence', 'meet-rsvp', 'cancellation-terms', 'charges'],
  },
  {
    id: 'saved-cards', section: 'payments', parent: 'payments', name: 'Saved cards & wallets', art: 'card',
    line: 'Card, Apple Pay, Google Pay.',
    aliases: ['payment method', 'apple pay', 'google pay', 'customer'],
    what: 'A person\'s saved payment methods per club account, used for meet sessions and renewals; wallets for one-off payments.',
    holds: ['Saved card per club', 'Default method'],
    sees: 'The person', manages: 'The person',
    rules: ['Cards are held by Stripe, never by Tap Leagues.'],
    related: ['account', 'charges'],
  },
  {
    id: 'pay-links', section: 'payments', parent: 'payment-account', name: 'Pay links & guest payers', art: 'link',
    line: 'Pay from a link, no account needed.',
    aliases: ['pay link', 'guest payment', 'pay by link'],
    what: 'An entry fee, a membership or a booking can be paid from a link. Somebody without an account pays as a guest payer.',
    holds: ['Pay link token per fee', 'Guest payer record'],
    sees: 'Whoever holds the link', manages: 'Club admins send them',
    rules: ['The link opens the real checkout with wallets and card.'],
    related: ['entry-fees', 'payment-request'],
  },
  {
    id: 'payment-terms', section: 'payments', parent: 'payment-account', name: 'Payment terms', art: 'document',
    line: 'The terms you agreed to, by version.',
    aliases: ['terms', 'entry fee terms', 'meet terms', 'club terms'],
    what: 'Each paid competition and meet has terms with a version. The version you agreed to is stored with your join or RSVP.',
    holds: ['Terms text and version per competition or meet', 'Acceptances per person'],
    sees: 'Everyone before they pay', manages: 'Club admins',
    rules: ['Changing the terms starts a new version; nobody is re-bound silently.'],
    related: ['cancellation-terms', 'type-terms', 'consents'],
  },
  {
    id: 'your-payments', section: 'payments', parent: 'payments', name: 'Your payments', art: 'receipt',
    line: 'Your receipts, what you owe, your cards.',
    aliases: ['my payments', 'statement', 'receipts', 'owed'],
    what: 'A member\'s own page: fees due, charges and receipts, saved cards, who you pay for.',
    holds: ['Fees due', 'Charges and receipts', 'Saved cards'],
    sees: 'The person', manages: 'The person',
    rules: ['Receipts link to Stripe\'s receipt.'],
    related: ['charges', 'entry-fees', 'saved-cards'],
  },
  {
    id: 'payments-admin', section: 'payments', parent: 'payments', name: 'Payments admin', art: 'clipboard', admin: true,
    line: 'Every charge, refund and fee in one place.',
    aliases: ['payments page', 'summary', 'actual fee rate'],
    what: 'The club\'s payments pages: charges, refunds, fees due, waive, and a summary with the actual fee rate over 90 days.',
    holds: ['Charges and refunds', 'Fees due by competition', 'Summary and fee estimates'],
    sees: 'Club admins', manages: 'Club admins',
    rules: ['Fee estimates use the club account\'s own country and currency.'],
    related: ['charges', 'entry-fees', 'payment-account'],
  },

  // ── Memberships & passes ─────────────────────────────────
  {
    id: 'memberships', section: 'memberships', parent: null, name: 'Memberships & passes', art: 'membercard',
    line: 'What a club sells or gives away to belong.',
    aliases: ['membership', 'pass', 'subscription', 'season ticket', 'renewal'],
    what: 'Opt-in per club. Membership types and passes with prices and renewal terms, applications with optional approval, a person\'s memberships with their billing, and entry requirements that plug into every competition.',
    holds: ['Membership types & passes', 'Applications', 'Current memberships & passes', 'Entry requirements per competition', 'Settings, import and the join widget'],
    sees: 'Everyone sees public types on the club page', manages: 'Club admins; switched on by Tap Leagues',
    rules: ['Getting one adds you to the club\'s players; lapsing never removes you.', 'Renewals are charged on the club\'s own Stripe account, on the dates the type sets.'],
    related: ['membership-type', 'application', 'membership', 'competition-rules', 'join-widget'],
  },
  {
    id: 'membership-type', section: 'memberships', parent: 'memberships', name: 'Membership types & passes', art: 'membercard',
    line: 'Senior, Junior, Social, a summer pass.',
    aliases: ['type', 'tier', 'plan', 'pass', 'age band', 'places limit'],
    what: 'A product the club defines. A pass is a membership type worded as a pass.',
    holds: ['Status: Draft, Live, Closed to new; Public or Hidden', 'Approval needed; age band (on join date or a fixed date); places limit; new-to-club only', 'Covers one person or a group (with slots)', 'Length: ongoing, set dates, or repeating blocks; mid-period pricing; covers a meet', 'Price options, terms, questions, health questionnaire', 'Renews as another type; next type on age-out; needs a child membership'],
    sees: 'Everyone (public types), club admins (all)', manages: 'Club admins',
    rules: ['Repeating blocks generate their own terms: never a new type per block.', 'Refund policy: none, in full within a set number of days, or for the unused part.', 'A pass covering a meet can be priced per sessions left.'],
    related: ['price-options', 'type-periods', 'type-terms', 'health-questionnaire', 'group-slots', 'competition-rules'],
  },
  {
    id: 'price-options', section: 'memberships', parent: 'membership-type', name: 'Price options', art: 'coin',
    line: 'Annual, quarterly, monthly; joining fee; free.',
    aliases: ['price', 'instalments', 'joining fee', 'minimum term', 'free'],
    what: 'The ways to pay for a type: all at once or in monthly or quarterly instalments, each with its own joining fee and minimum term.',
    holds: ['Amount, instalments, joining fee, minimum term'],
    sees: 'Everyone (public types)', manages: 'Club admins',
    rules: ['Empty price means free.', 'Stripe minimums are checked on save.'],
    related: ['membership-period', 'membership-type'],
  },
  {
    id: 'type-periods', section: 'memberships', parent: 'membership-type', name: 'Terms (blocks)', art: 'calendar',
    line: 'Generated periods for repeating blocks.',
    aliases: ['period', 'block', 'season dates', 'fixed period'],
    what: 'For set-dates and repeating-block types: the shared terms everyone is on (1 Apr to 31 Mar; 12 weeks from 6 Jan, then the next 12).',
    holds: ['Start and end, on sale from'],
    sees: 'Everyone (public types)', manages: 'Generated; club admins set the pattern',
    rules: ['Week, month, quarter or year units.'],
    related: ['membership-type'],
  },
  {
    id: 'type-terms', section: 'memberships', parent: 'membership-type', name: 'Terms & conditions', art: 'document',
    line: 'Club terms from a template, versioned.',
    aliases: ['membership terms', 'template', 'club section', 'add-on', 'add-ons'],
    what: 'The type\'s terms, built from a Tap Leagues template with the club\'s own section and any add-ons for its sports, with a version each member agreed to.',
    holds: ['Template and version, club section, terms version', 'Add-ons: extra sections per kind of sport (racket, team, cue and target sports)'],
    sees: 'Everyone before applying', manages: 'Club admins; templates by Tap Leagues',
    rules: ['When Tap Leagues updates the template, the club\'s own section and add-ons stay as they are.'],
    related: ['payment-terms'],
  },
  {
    id: 'health-questionnaire', section: 'memberships', parent: 'application', name: 'Application & health questions', art: 'clipboard',
    line: 'Questions at apply, from templates.',
    aliases: ['questionnaire', 'medical', 'health questions', 'questions'],
    what: 'Optional questions on the application, and a health questionnaire from a per-sport template the club can tweak.',
    holds: ['Questions and required flags', 'Medical template, version and overrides', 'Flag-for-admin answers'],
    sees: 'Applicants (the questions), permitted admins (the answers)', manages: 'Club admins; templates by Tap Leagues',
    rules: ['Children answer their own health questions; for under-16s the parent reviews them.'],
    related: ['health-answers'],
  },
  {
    id: 'group-slots', section: 'memberships', parent: 'membership-type', name: 'Group slots', art: 'people',
    line: 'A type that covers several people.',
    aliases: ['family', 'household', 'group membership', 'slot'],
    what: 'A type covering a group: slots with their own age rules, a lead member and a buyer.',
    holds: ['Slots and who fills them'],
    sees: 'Everyone (public types)', manages: 'Club admins',
    rules: ['Each person in the group gets their own membership record.'],
    related: ['membership-group'],
  },
  {
    id: 'application', section: 'memberships', parent: 'membership-type', name: 'Applications', art: 'clipboard',
    line: 'Pending, approved, declined; a card hold while waiting.',
    aliases: ['apply', 'approval', 'card hold', 'pre-authorised'],
    what: 'Somebody applies for a type. If the type needs approval, the card is held and captured on approval; declining releases the hold.',
    holds: ['Status: pending, approved, declined, withdrawn, expired, awaiting parent, awaiting link', 'Answers, chosen price option, group people', 'Parent or carer consent', 'Ask someone else to pay'],
    sees: 'The applicant and club admins', manages: 'Club admins',
    rules: ['Admins are told of each application and of holds about to lapse.', 'An under-18\'s application waits for the parent before the club sees it.'],
    related: ['parent-consent', 'payment-request', 'membership'],
  },
  {
    id: 'parent-consent', section: 'memberships', parent: 'application', name: 'Parent or carer consent', art: 'shield',
    line: 'Agreed by email, no account needed.',
    aliases: ['parent', 'carer', 'consent', 'under 18', 'parent play'],
    what: 'An under-18 names a parent or carer who agrees by email, can pay from the same email, and for under-16s reviews the health answers.',
    holds: ['Parent name, email, relationship', 'Agreed, declined or expired', 'Parent pays; can review health answers'],
    sees: 'The parent (by email), the child, permitted admins', manages: 'The parent',
    rules: ['A parent\'s own linked membership (Parent Play) ends with the child\'s.'],
    related: ['juniors', 'health-answers'],
  },
  {
    id: 'payment-request', section: 'memberships', parent: 'application', name: 'Ask someone else to pay', art: 'receipt',
    line: 'A payer by email; renewals go to them.',
    aliases: ['payer', 'pay for me', 'payment request'],
    what: 'The applicant or member asks somebody else to pay. The payer signs in or registers, pays, and gets the renewals until they stop.',
    holds: ['Payer email and name, token, status', 'Accepted terms version'],
    sees: 'The member and the payer', manages: 'The two people',
    rules: ['If the payer stops, the next renewal goes back to the member.'],
    related: ['bill-to', 'pay-links', 'membership-period'],
  },
  {
    id: 'membership', section: 'memberships', parent: 'application', name: 'A person\'s membership or pass', art: 'membercard',
    line: 'Awaiting payment, active, past due, lapsed, cancelled, expired.',
    aliases: ['current membership', 'holder', 'my membership', 'cancel', 'auto renew'],
    what: 'One record per person per type they hold. A player can hold several.',
    holds: ['Status, current period, started, minimum term', 'Auto-renew, cancel at period end', 'Source: applied, admin added, imported; settled offline', 'Group and slot; linked child membership', 'Billing periods'],
    sees: 'The holder and club admins', manages: 'The holder (cancel when allowed) and club admins',
    rules: ['Past due stays current inside the retry window; then it lapses and can be restored within 30 days.', 'Age-out offers the next type.', 'Admin-added memberships are paid outside or no charge.'],
    related: ['membership-period', 'players', 'membership-type', 'health-answers'],
  },
  {
    id: 'membership-period', section: 'memberships', parent: 'membership', name: 'Billing periods', art: 'calendar',
    line: 'Scheduled, paid, failed, waived.',
    aliases: ['renewal', 'invoice', 'ledger', 'paid outside', 'no charge', 'disputed'],
    what: 'Each term of a membership and how it was settled.',
    holds: ['Scheduled, charging, paid, failed, refunded, waived, settled offline, authorised, disputed', 'The charge'],
    sees: 'The holder and club admins', manages: 'Automatic; admins waive or mark settled offline',
    rules: ['Renewal reminders go out before the date; failures retry with an email.', 'A dispute keeps the membership current and tells admins.'],
    related: ['charges', 'line-items'],
  },
  {
    id: 'renewal', section: 'memberships', parent: 'membership-period', name: 'Renewal', art: 'swap',
    line: 'The next period, charged on the type\'s dates.',
    aliases: ['renew', 'auto-renew', 'renewal reminder', 'lapse', 'expired'],
    what: 'An ongoing or repeating membership renews into its next billing period: a reminder goes out before the date, the saved card is charged on it, and a failure retries with an email. A type can renew as another type, or move a junior to the next type when they age out.',
    holds: ['Reminder before the date', 'The charge on the saved card, retried on failure', 'Renews as the same or another type; next type on age-out', 'Lapsed when nothing paid'],
    sees: 'The holder and club admins', manages: 'Automatic; admins can waive or settle offline',
    rules: ['Lapsing never removes you from the club\'s players.', 'A failed renewal keeps the membership current while it retries, then lapses.'],
    related: ['membership-period', 'membership', 'saved-cards', 'membership-type'],
  },
  {
    id: 'health-answers', section: 'memberships', parent: 'membership', name: 'Health answers', art: 'shield',
    line: 'Encrypted; seen only by permitted admins.',
    aliases: ['medical answers', 'health details', 'access log'],
    what: 'A member\'s answers to the health questionnaire, encrypted, with an access log and a delete-after date.',
    holds: ['Answers (encrypted), flagged answers', 'Who looked and why', 'Delete after'],
    sees: 'The member; admins with Can see health details', manages: 'The member',
    rules: ['Every access is recorded.'],
    related: ['club-admins', 'health-questionnaire'],
  },
  {
    id: 'membership-group', section: 'memberships', parent: 'memberships', name: 'Groups', art: 'people',
    line: 'A lead and the people in a group type.',
    aliases: ['family', 'household', 'group'],
    what: 'The people covered by one group membership: a lead, a buyer, each with their own record.',
    holds: ['Lead, buyer, members, slots'],
    sees: 'The group and club admins', manages: 'The lead and club admins',
    rules: ['Leaving the group ends that person\'s record, not the group\'s.'],
    related: ['group-slots', 'membership'],
  },
  {
    id: 'competition-rules', section: 'memberships', parent: 'memberships', name: 'Entry requirements', art: 'ticket',
    line: 'Who can enter a competition and what they pay.',
    aliases: ['members only', 'membership needed', 'member price', 'eligibility'],
    what: 'Per competition: Anyone, or Membership or pass needed (any, or the ticked types), plus a different price per membership type.',
    holds: ['Eligibility: anyone, any membership, ticked types', 'Member price per type'],
    sees: 'Everyone before joining', manages: 'Club admins (in each competition\'s setup)',
    rules: ['Every player in a team must qualify; the lowest member price applies.', 'A quick-add form creates and ticks a type in place.'],
    related: ['membership-type', 'entry-fees', 'league', 'ladder', 'tournament', 'americano', 'meet'],
  },
  {
    id: 'membership-import', section: 'memberships', parent: 'memberships', name: 'Import existing memberships', art: 'spreadsheet', admin: true,
    line: 'A spreadsheet in; types, statuses and dates matched.',
    aliases: ['import', 'export', 'spreadsheet', 'csv', 'clubspark', 'spond'],
    what: 'Upload the club\'s current membership list. Columns, types, statuses and payment methods are guessed (AI-assisted) and reviewed before anything is created. Export too.',
    holds: ['File, column map, type map, proposed new types', 'Review and apply'],
    sees: 'Club admins', manages: 'Club admins',
    rules: ['The model sees headers and category values, never names or health answers.'],
    related: ['member-import', 'membership'],
  },
  {
    id: 'membership-settings', section: 'memberships', parent: 'memberships', name: 'Settings', art: 'wrench', admin: true,
    line: 'Who can see players; renewals; commission.',
    aliases: ['membership settings', 'grace period', 'pause renewals'],
    what: 'The club-wide switches for memberships.',
    holds: ['Who can see the club\'s players: everyone or membership holders, with a grace period', 'Pause renewals'],
    sees: 'Club admins', manages: 'Club admins',
    rules: ['Players without a membership are told at the switch-on and near the grace period\'s end.'],
    related: ['club-visibility'],
  },

  // ── Playing areas & bookings ───────────────────────────────────────────
  {
    id: 'playing-areas', section: 'bookings', parent: null, name: 'Playing areas', art: 'court',
    line: 'Courts, tables, boards, lanes.',
    aliases: ['court', 'table', 'board', 'lane', 'pitch', 'venue', 'area'],
    what: 'The places a club plays. Matches are scheduled on them; with bookings on, some can be booked.',
    holds: ['Name, description, sport(s), order', 'Can be booked', 'Booking rules, bookings, closures'],
    sees: 'Everyone in the club', manages: 'Club admins',
    rules: ['Creating an area stays simple, with booking off.', 'The sport\'s word is used: court availability, table availability.'],
    related: ['match', 'area-group', 'booking', 'closures', 'booking-widgets'],
  },
  {
    id: 'area-group', section: 'bookings', parent: 'playing-areas', name: 'Booking rules', art: 'timetable',
    line: 'Rules shared by a group of areas.',
    aliases: ['area group', 'slot length', 'cancel cut-off', 'rules', 'group of courts'],
    what: 'A group of playing areas (the courts, the tables) sharing slot length, cancel cut-off, booking hours, who can book, and changes for a day.',
    holds: ['Slot length and cancel cut-off', 'Booking hours', 'Who can book', 'Changes for a day'],
    sees: 'Members see the result on the calendar', manages: 'Club admins',
    rules: ['Areas are open play by default; booking is needed only inside painted hours.'],
    related: ['booking-hours', 'who-can-book', 'day-changes', 'playing-areas'],
  },
  {
    id: 'booking-hours', section: 'bookings', parent: 'area-group', name: 'Booking hours', art: 'timetable',
    line: 'Weekly bands: booking needed, matches only, closed.',
    aliases: ['hours', 'opening hours', 'league night', 'matches only', 'closed'],
    what: 'Bands painted on a weekly grid. Blank is open play; a band says booking needed, matches only (league night) or closed.',
    holds: ['Day, start, end, kind'],
    sees: 'Everyone on the calendar', manages: 'Club admins',
    rules: ['Matches only means only matches and admins can use the areas in that time.'],
    related: ['area-group', 'match-booking'],
  },
  {
    id: 'who-can-book', section: 'bookings', parent: 'area-group', name: 'Who can book', art: 'people',
    line: 'Per audience: longest, how far ahead, how many, price.',
    aliases: ['audience', 'guests', 'visitors', 'max duration', 'days ahead', 'price'],
    what: 'One row per audience: people with no membership, each membership type, guests added by name, and visitors from the public page.',
    holds: ['Can book, longest booking, all day, days ahead, max active bookings, price'],
    sees: 'Members see what applies to them', manages: 'Club admins',
    rules: ['Empty price means free.', 'Admins bypass every rule.'],
    related: ['membership-type', 'visitor-booking'],
  },
  {
    id: 'day-changes', section: 'bookings', parent: 'area-group', name: 'Changes for a day', art: 'calendar',
    line: 'One day\'s hours changed, with a note.',
    aliases: ['override', 'closed all day', 'open play', 'bank holiday'],
    what: 'An ad-hoc change for one date: booking needed, matches only, open play or closed, in the time it covers, with a note members see.',
    holds: ['Date, time, kind, note'],
    sees: 'Everyone on the calendar', manages: 'Club admins',
    rules: ['Replace a day\'s changes in one save, with a preview.', 'Closed all day reads "Closed all day".'],
    related: ['area-group', 'closures'],
  },
  {
    id: 'booking-settings', section: 'bookings', parent: 'playing-areas', name: 'Booking settings', art: 'wrench', admin: true,
    line: 'Match bookings, holds, who pays, release time.',
    aliases: ['bookings on', 'match booking days ahead', 'match payer', 'split', 'release time'],
    what: 'Club-wide booking switches.',
    holds: ['Bookings on or off', 'Match bookings: days ahead, holds for challenge proposals', 'Who pays for a match booking: one payer or split', 'Release time (when a day opens), currency'],
    sees: 'Club admins', manages: 'Club admins',
    rules: ['Turning bookings on needs nothing else: free, open areas keep working.'],
    related: ['match-booking', 'booking'],
  },
  {
    id: 'booking', section: 'bookings', parent: 'who-can-book', name: 'Bookings', art: 'calendar-check',
    line: 'Play, match, meet hold, admin, visitor.',
    aliases: ['book a court', 'reserve', 'slot', 'release', 'no show', 'guest'],
    what: 'A slot on an area. A member books for play; scheduling books for a match; a meet holds its areas; admins book for anyone; visitors book from the public page.',
    holds: ['Kind, start, end, area, who booked', 'Status: queued, held, pending payment, confirmed, cancelled, released, no-show', 'Guests added by name, price, the charge'],
    sees: 'The booker (and the grid shows busy)', manages: 'The booker (cancel, one-tap Release); club admins',
    rules: ['Confirmation and a club-made cancellation are the record and never switch off; the hour-before reminder can be.', 'Queued, held, pending and confirmed all block the slot.', 'Booking inside the cancel cut-off warns you first: cancelling it later still costs the full price.'],
    related: ['match-booking', 'visitor-booking', 'meet-occurrence', 'charges', 'booking-widgets'],
  },
  {
    id: 'match-booking', section: 'bookings', parent: 'booking', name: 'Match bookings', art: 'scoreboard',
    line: 'Made by scheduling, never by hand.',
    aliases: ['match slot', 'queued', 'challenge hold', 'inside the window'],
    what: 'Confirming a match time books the area. Too far ahead, it queues until that day opens; a challenge proposal can hold the slot until the reply.',
    holds: ['Queued until, payer, hold'],
    sees: 'The match\'s players and admins', manages: 'Automatic; club admins',
    rules: ['A queued match that finds no free area when its day opens tells admins.', 'Whoever confirms the time picks the payer (themselves by default) or the sides split.'],
    related: ['scheduling', 'challenge', 'booking-settings'],
  },
  {
    id: 'visitor-booking', section: 'bookings', parent: 'booking', name: 'Visitor bookings', art: 'globe',
    line: 'No account: book from the public page.',
    aliases: ['visitor', 'public booking', 'book page', 'code'],
    what: 'Somebody with no account books from the club\'s public booking page (or the website widget), confirms a free booking with an emailed code, or pays.',
    holds: ['Visitor name and email, code, payment'],
    sees: 'The visitor (by email) and admins', manages: 'Club admins',
    rules: ['Whether visitors can book, and at what price, is a row in Who can book.'],
    related: ['who-can-book', 'booking-widgets'],
  },
  {
    id: 'closures', section: 'bookings', parent: 'playing-areas', name: 'Closures', art: 'pause',
    line: 'Maintenance, a tournament, a private event.',
    aliases: ['freeze area', 'maintenance', 'closed', 'out of use'],
    what: 'An area out of use for a while, with a reason and a note members see.',
    holds: ['From, until, reason, note'],
    sees: 'Everyone on the calendar', manages: 'Club admins',
    rules: ['Existing bookings in a closure are cancelled and refunded in full.'],
    related: ['day-changes', 'booking'],
  },

  // ── Communication ──────────────────────────────────────────────────────
  {
    id: 'communication', section: 'communication', parent: null, siblings: true, name: 'Notifications & messages', art: 'bell',
    line: 'How Tap Leagues and your club reach you.',
    aliases: ['comms', 'messages', 'alerts'],
    what: 'Pushes and emails about what matters to you, chats, announcements from your club, the home feed, and email from admins.',
    holds: ['Notifications', 'Chats', 'Announcements', 'Home feed', 'Email to players', 'Message reports', 'Tappy', 'Share links'],
    sees: 'Everyone', manages: 'You (preferences); club admins (announcements, email)',
    rules: ['Nothing marketing arrives until you say yes.'],
    related: ['notifications', 'chats', 'announcements', 'home-feed'],
  },
  {
    id: 'notifications', section: 'communication', parent: 'communication', name: 'Notifications', art: 'bell',
    line: 'Push and email, per type, your choice.',
    aliases: ['push notification', 'email', 'reminder', 'alert'],
    what: 'Challenges, match scheduling and reminders, results to approve, meet RSVPs and offers, fees due, receipts, follow requests, ladder inactivity, memberships and bookings.',
    holds: ['One type per event, grouped: matches, challenges, meets, payments, joining, following, memberships, bookings', 'Email and push per type'],
    sees: 'The person', manages: 'The person; admins get the admin types',
    rules: ['Confirmations that are the record of a purchase never switch off.'],
    related: ['notification-preferences', 'challenge', 'match', 'meet-rsvp'],
  },
  {
    id: 'chats', section: 'communication', parent: 'communication', name: 'Chats', art: 'chat',
    line: 'One-to-one, group, and one per competition.',
    aliases: ['messaging', 'conversation', 'group chat', 'direct message', 'unread'],
    what: 'Direct and group chats, plus a chat on every club, league cycle, division, ladder, tournament, meet, americano cycle and challenge.',
    holds: ['Members, messages, read state', 'Unread count', 'Deleted messages show as deleted'],
    sees: 'Its members', manages: 'Its members; admins moderate',
    rules: ['Blocks are respected in direct chats.', 'In the app, an Open row jumps from a chat to its competition.'],
    related: ['message-reports', 'blocking', 'league-division', 'challenge-chat'],
  },
  {
    id: 'announcements', section: 'communication', parent: 'communication', name: 'Announcements', art: 'megaphone',
    line: 'A card on the home feed from your club.',
    aliases: ['notice', 'news', 'pinned', 'sponsored'],
    what: 'Club admins publish cards to their players\' home feeds: title, image, link, pinned or not, with a publish and expiry time and an audience filter.',
    holds: ['Title, body, image, link', 'Publish at, expires, pinned, dismissible', 'Audience filters, sponsored flag'],
    sees: 'The audience', manages: 'Club admins',
    rules: ['Sponsored announcements say who declared it.'],
    related: ['home-feed', 'announcements-admin'],
  },
  {
    id: 'home-feed', section: 'communication', parent: 'announcements', name: 'Home feed', art: 'grid',
    line: 'Results, challenges, cards, what needs you.',
    aliases: ['timeline', 'home', 'feed', 'cards', 'dismiss'],
    what: 'The home screen: things that happened in your competitions, cards that need an answer (approve a result, a challenge, a quick question, combine accounts), announcements.',
    holds: ['Timeline items with references', 'Dismissals per person'],
    sees: 'The person', manages: 'Automatic',
    rules: ['Only approved results appear.'],
    related: ['announcements', 'result-approval'],
  },
  {
    id: 'email-players', section: 'communication', parent: 'communication', name: 'Email players', art: 'envelope', admin: true,
    line: 'A mail to the club or a competition.',
    aliases: ['mailer', 'club email', 'bulk email', 'sent mail'],
    what: 'Admins email a club, a competition or a filtered set of players. Every send is kept with its recipients.',
    holds: ['Subject, body, filters', 'Sent mail and recipients'],
    sees: 'Club admins', manages: 'Club admins',
    rules: ['Suppressed addresses (bounces, complaints) are skipped.'],
    related: ['email-preferences'],
  },
  {
    id: 'email-preferences', section: 'communication', parent: 'notifications', name: 'Email preferences & suppressions', art: 'envelope',
    line: 'The unsubscribe link, and bounced addresses.',
    aliases: ['unsubscribe', 'bounce', 'suppression', 'email log'],
    what: 'Every email carries a link to your preferences. Addresses that bounce or complain are suppressed for everyone\'s protection.',
    holds: ['Preference link version', 'Suppressions with a reason', 'Email log'],
    sees: 'The person', manages: 'The person',
    rules: ['A suppressed player is flagged to admins.'],
    related: ['notification-preferences'],
  },
  {
    id: 'message-reports', section: 'communication', parent: 'chats', name: 'Message reports', art: 'flag',
    line: 'Report a message; the club reviews it.',
    aliases: ['report', 'abuse', 'content report', 'moderation'],
    what: 'Anyone can report a chat message. A snapshot is kept, club admins review it (or Tap Leagues when no club reviewer exists).',
    holds: ['Reason, details, snapshot of the message', 'Review status and notes'],
    sees: 'The reporter (their own) and club admins', manages: 'Club admins; Tap Leagues when the report is about an admin',
    rules: ['Reports about an admin go to Tap Leagues.'],
    related: ['chats', 'reports-admin', 'blocking'],
  },
  {
    id: 'assistant', section: 'communication', parent: 'help-centre', name: 'Tappy', art: 'robot',
    line: 'Ask how to do anything in Tap Leagues, in your language.',
    aliases: ['tappy', 'help assistant', 'ai assistant', 'ai helper', 'chatbot', 'help', 'support'],
    what: 'Tappy is the AI helper on the Help page, in the top bar and in the app. It answers questions about using Tap Leagues and replies in your language. Separate from the setup assistant.',
    holds: ['Conversations and their cost', 'Thumbs up and down'],
    sees: 'Signed-in people it is switched on for', manages: 'Tap Leagues',
    rules: ['It answers questions; it never changes your club or your data.'],
    related: ['setup-assistant'],
  },
  {
    id: 'help-centre', section: 'communication', parent: 'communication', name: 'Help & explainers', art: 'document',
    line: 'How each format works, what Tap Leagues does, this map.',
    aliases: ['help centre', 'help center', 'formats', 'features', 'explainer', 'how it works', 'system map'],
    what: 'The public pages that explain Tap Leagues: one page per format with a short explainer, the Features page, the Help centre and this map of how it all fits together.',
    holds: ['Format pages and explainers (also opened from competition pages)', 'Features', 'Help centre and Tappy', 'This map'],
    sees: 'Anyone', manages: 'Tap Leagues',
    rules: ['The same explainer opens from a competition\'s page and from the admin\'s format list.'],
    related: ['assistant', 'public-pages'],
  },
  {
    id: 'short-links', section: 'communication', parent: 'communication', name: 'Share links', art: 'link',
    line: 'Short links for posters and sharing, with clicks counted.',
    aliases: ['short link', 'tapl.gs', 'share', 'qr'],
    what: 'Short links behind invite links, posters and shares, with a title and image for previews and a click count.',
    holds: ['Short code, target, title, description, image', 'Clicks'],
    sees: 'Whoever holds the link', manages: 'Automatic',
    rules: ['Custom slugs are possible.'],
    related: ['join-link'],
  },

  // ── Widgets (X146: was "On your website"; the id/section stay) ─────────
  {
    id: 'website', section: 'website', parent: null, name: 'Widgets', art: 'globe',
    line: 'Your club\'s site shows live Tap Leagues data.',
    aliases: ['embed', 'widget', 'iframe', 'wix', 'wordpress', 'squarespace', 'website'],
    what: 'Widgets made from each section of the admin, one line of code each, allowed only on the websites you list, linking back into Tap Leagues to join, book or pay.',
    holds: ['Widget types', 'Allowed websites', 'Embed code', 'Links back to Tap Leagues', 'Website verification', 'Public pages'],
    sees: 'Visitors to the club\'s website', manages: 'Club admins',
    rules: ['A widget shows what a club member would see, never more.'],
    related: ['widgets', 'allowed-domains', 'embed-code', 'link-back'],
  },
  // X150 (R72 L8): the area is "Widgets"; this part is what you can embed, so it has its own name
  {
    id: 'widgets', section: 'website', parent: 'embed-code', name: 'Widget types', art: 'puzzle',
    line: 'Standings, positions, draws, results, stats, booking, joining.',
    aliases: ['widget', 'embed', 'standings widget', 'ladder widget'],
    what: 'Each widget shows one thing from the club on the club\'s own site, in the club\'s colour and a chosen style, and can be disabled at any time.',
    holds: ['Competition widgets', 'Stats leaderboards', 'Booking widgets', 'Join a membership or pass', 'Colour, style, allowed websites'],
    sees: 'Visitors to the club\'s website', manages: 'Club admins',
    rules: ['Widgets are generated from the section they belong to.'],
    related: ['widgets-competition', 'widgets-stats', 'booking-widgets', 'join-widget'],
  },
  {
    id: 'widgets-competition', section: 'website', parent: 'widgets', name: 'Competition widgets', art: 'table',
    line: 'Standings, ladder positions, draws and results.',
    aliases: ['standings widget', 'ladder widget', 'draw widget', 'results widget', 'recent results'],
    what: 'Live standings for a box league division, ladder positions, a tournament draw, recent results across the club, americano standings.',
    holds: ['The competition (and division) it shows'],
    sees: 'Visitors to the club\'s website', manages: 'Club admins',
    rules: ['Tapping through opens the competition on Tap Leagues.'],
    related: ['league-standings', 'ladder-positions', 'bracket', 'americano-standings', 'match'],
  },
  {
    id: 'widgets-stats', section: 'website', parent: 'widgets', name: 'Stats leaderboards', art: 'chart',
    line: 'Player stats for the club or one competition.',
    aliases: ['stats widget', 'leaderboard widget'],
    what: 'A leaderboard of player stats for the club, a meet, a ladder, a box league, a tournament or an americano league.',
    holds: ['What it ranks and for which competition'],
    sees: 'Visitors to the club\'s website', manages: 'Club admins',
    rules: ['Hidden players read as "Private player".'],
    related: ['stats', 'player-stats'],
  },
  {
    id: 'booking-widgets', section: 'website', parent: 'widgets', name: 'Booking widgets', art: 'timetable',
    line: 'Book a playing area; what is available now.',
    aliases: ['availability widget', 'court availability', 'book widget'],
    what: 'Two widgets from the Bookings section: available slots for the next few days that open your booking page, and a live "available now" board.',
    holds: ['Book a playing area', 'Availability'],
    sees: 'Visitors to the club\'s website', manages: 'Club admins',
    rules: ['Booking happens on Tap Leagues as a new page (visitors sign in or book as a guest), so it works on Wix, WordPress and Squarespace.'],
    related: ['booking', 'visitor-booking', 'playing-areas', 'link-back'],
  },
  {
    id: 'join-widget', section: 'website', parent: 'widgets', name: 'Join a membership or pass', art: 'membercard',
    line: 'Your live public types with a Join button.',
    aliases: ['membership widget', 'join widget', 'sign up widget'],
    what: 'From the Memberships & passes section: the club\'s live, public memberships and passes. Joining and paying happen on Tap Leagues.',
    holds: ['The types shown'],
    sees: 'Visitors to the club\'s website', manages: 'Club admins',
    rules: ['Hidden types never appear.'],
    related: ['membership-type', 'application', 'link-back'],
  },
  {
    id: 'allowed-domains', section: 'website', parent: 'website', name: 'Allowed websites', art: 'shield',
    line: 'Only the sites you list can show your widgets.',
    aliases: ['domain allowlist', 'allowed domains', 'frame ancestors', 'blocked'],
    what: 'Per widget, the websites allowed to embed it. Anywhere else the widget shows a neutral unavailable state.',
    holds: ['Hostnames, with or without a port'],
    sees: 'Club admins', manages: 'Club admins',
    rules: ['A host matches exactly, or as a subdomain of a port-less entry.', 'Enforced by the browser (frame-ancestors) and by the page itself.'],
    related: ['widgets', 'embed-code'],
  },
  {
    id: 'embed-code', section: 'website', parent: 'allowed-domains', name: 'Embed code', art: 'code',
    line: 'One line of script; the widget sizes itself.',
    aliases: ['script tag', 'copy code', 'install'],
    what: 'Each widget gives one `<script>` line to paste into the site. It loads the widget in a frame that reports its height, so there are no scrollbars.',
    holds: ['The one-line code per widget'],
    sees: 'Club admins', manages: 'Club admins',
    rules: ['Regenerate a widget\'s key and the old code stops working.'],
    related: ['widgets', 'allowed-domains'],
  },
  {
    id: 'link-back', section: 'website', parent: 'website', name: 'Links back to Tap Leagues', art: 'link',
    line: 'Join, book and pay happen on Tap Leagues.',
    aliases: ['return link', 'back to site', 'open on tap leagues'],
    what: 'Widgets open the club page, booking page or join page on tapleagues.com as a new page, carrying the page they sat on as a return link.',
    holds: ['Return link (https only, checked against the allowed websites)'],
    sees: 'Visitors to the club\'s website', manages: 'Automatic',
    rules: ['Booking, join and pay pages can never be framed by another site.'],
    related: ['booking-widgets', 'join-widget', 'club-page'],
  },
  {
    id: 'website-verification', section: 'website', parent: 'website', name: 'Website verification', art: 'check', admin: true,
    line: 'Prove the club owns its website.',
    aliases: ['verify domain', 'website url', 'verification token'],
    what: 'The club\'s website address in settings, verified with a token so Tap Leagues can trust links to it.',
    holds: ['Website address, token, verified at'],
    sees: 'Club admins', manages: 'Club admins',
    rules: ['A verified club shows a badge.'],
    related: ['club-settings'],
  },
  {
    id: 'public-pages', section: 'website', parent: 'website', name: 'Public pages', art: 'globe',
    line: 'Club, competition and player pages, when public.',
    aliases: ['seo', 'public', 'sitemap', 'formats pages'],
    what: 'Public clubs and competitions have indexable pages on tapleagues.com, as do the Formats and Features pages.',
    holds: ['Club page, competition pages, player pages', 'Open Graph images for sharing'],
    sees: 'Anyone, per visibility', manages: 'Visibility settings',
    rules: ['Private pages show a locked page with the harmless facts.'],
    related: ['club-page', 'club-visibility', 'profile-privacy'],
  },

  // ── Clubhouse TV ───────────────────────────────────────────────────────
  {
    id: 'tv', section: 'tv', parent: null, name: 'Clubhouse TV', art: 'tv',
    line: 'A live screen on the clubhouse wall.',
    aliases: ['display', 'screen', 'kiosk', 'tv', 'wall screen'],
    what: 'Public screens per club at /tv/{key}: a rotating feature area with an always-on sidebar of latest results, what is coming up and how to join.',
    holds: ['Displays', 'Panels per competition type', 'Live scores and result celebrations'],
    sees: 'Anyone in the clubhouse (shows what a member would see)', manages: 'Club admins',
    rules: ['Designed at 1920 by 1080, scaled to any screen, portrait re-flows.', 'Never indexed, never cached.'],
    related: ['tv-display', 'tv-panels', 'live-scoring'],
  },
  {
    id: 'tv-display', section: 'tv', parent: 'tv', name: 'Displays', art: 'tv',
    line: 'One per screen: theme, style, what to show.',
    aliases: ['display', 'broadcast', 'clubhouse theme', 'accent colour', 'ticker'],
    what: 'Each display has a public key, a theme (Broadcast dark or Clubhouse light), a style, an accent colour, full names or initials, a ticker, live scores, celebrations, the league view and join codes.',
    holds: ['Key, theme, style, accent colour', 'Show full names, ticker, live scores, celebrate results', 'Show club and competition join codes', 'Panel overrides', 'Disabled'],
    sees: 'Anyone with the key', manages: 'Club admins',
    rules: ['Under-18s show as "First L."; hidden players as "Private player".'],
    related: ['tv-panels', 'join-link', 'live-scoring'],
  },
  {
    id: 'tv-panels', section: 'tv', parent: 'tv-display', name: 'Panels', art: 'grid',
    line: 'Standings, ladders, draws, meets, results, join.',
    aliases: ['panel', 'section', 'pinned', 'hidden', 'seconds'],
    what: 'The rotating sections of a display, one per competition or kind, each pinnable, hideable, reorderable and timed.',
    holds: ['Order, hidden, pinned, seconds per panel'],
    sees: 'Anyone watching', manages: 'Club admins',
    rules: ['Short panels pack two to a column.'],
    related: ['league-standings', 'ladder-positions', 'bracket', 'meet-occurrence', 'match'],
  },

  // ── Admin tools ────────────────────────────────────────────────────────
  {
    id: 'admin', section: 'admin', parent: null, name: 'Club admin', art: 'wrench', admin: true,
    line: 'Everything an admin runs the club with.',
    aliases: ['admin area', 'console', 'manage', 'dashboard'],
    what: 'The admin side of the club: dashboard, players, competitions by format, payments, memberships and passes, playing areas, chats, announcements, widgets, Clubhouse TV, reports.',
    holds: ['Admin dashboard', 'Players admin and import', 'Reports and follow approvals', 'Setup assistant', 'Archive', 'PDF export'],
    sees: 'Club admins', manages: 'Club admins',
    rules: ['Admin actions sit in each page\'s menu and Manage links.'],
    related: ['admin-dashboard', 'players-admin', 'member-import', 'setup-assistant'],
  },
  {
    id: 'admin-dashboard', section: 'admin', parent: 'admin', name: 'Admin dashboard', art: 'grid', admin: true,
    line: 'What needs you, counts, competitions by format.',
    aliases: ['home', 'overview', 'to-do', 'checklist'],
    what: 'The club admin home: to-dos (results to approve, join requests, people waiting to be placed, requirements due), counts, and a card per format.',
    holds: ['To-dos', 'Setup checklist for new clubs', 'Format cards'],
    sees: 'Club admins', manages: 'Club admins',
    rules: ['A club that has not reviewed its visibility is nudged here.'],
    related: ['setup-guide', 'join-requests', 'waiting-area', 'result-approval'],
  },
  {
    id: 'players-admin', section: 'admin', parent: 'admin', name: 'Players admin', art: 'people', admin: true,
    line: 'Add, edit, make admin, remove, claim links.',
    aliases: ['members admin', 'add player', 'remove player', 'make admin'],
    what: 'The club\'s players from the admin side: add by name or email, edit details, mark juniors, make admins, remove, send claim links.',
    holds: ['Add and invite', 'Edit, junior, admin, remove', 'Claim links for players without an account'],
    sees: 'Club admins', manages: 'Club admins',
    rules: ['Removing from the club leaves history in place.'],
    related: ['players', 'managed-players', 'member-import'],
  },
  {
    id: 'member-import', section: 'admin', parent: 'players-admin', name: 'Import players from a spreadsheet', art: 'spreadsheet', admin: true,
    line: 'Upload, match columns, review, import.',
    aliases: ['csv import', 'spreadsheet', 'bulk add', 'import members'],
    what: 'Any CSV or spreadsheet. Columns are guessed (keywords, then cell shapes, then AI fills gaps), rows fixed or skipped inline, invites sent now or held, and a report of what was added, skipped and merged.',
    holds: ['File, column map, rows and corrections', 'Report'],
    sees: 'Club admins', manages: 'Club admins',
    rules: ['Duplicates are flagged before anything is created.'],
    related: ['players-admin', 'membership-import'],
  },
  {
    id: 'reports-admin', section: 'admin', parent: 'admin-dashboard', name: 'Reports', art: 'flag', admin: true,
    line: 'Message reports waiting for the club.',
    aliases: ['moderation', 'review report'],
    what: 'The club\'s queue of reported messages, with the snapshot and a place for review notes.',
    holds: ['Open and reviewed reports'],
    sees: 'Club admins', manages: 'Club admins',
    rules: ['A report with no club reviewer goes to Tap Leagues.'],
    related: ['message-reports'],
  },
  {
    id: 'follow-approvals', section: 'admin', parent: 'players-admin', name: 'Follow requests for managed players', art: 'link', admin: true,
    line: 'You decide for players without an account.',
    aliases: ['follow approval', 'managed player follow'],
    what: 'Somebody wants to follow a player the club manages; an admin accepts or declines.',
    holds: ['Pending requests'],
    sees: 'Club admins', manages: 'Club admins',
    rules: ['Adults only, as for every follow.'],
    related: ['follows', 'managed-players'],
  },
  {
    id: 'announcements-admin', section: 'admin', parent: 'admin', name: 'Announcements admin', art: 'megaphone', admin: true,
    line: 'Write, pin, schedule, expire.',
    aliases: ['publish announcement', 'pin'],
    what: 'Where club admins write announcements for their players\' home feeds.',
    holds: ['Drafts, scheduled and live announcements'],
    sees: 'Club admins', manages: 'Club admins',
    rules: ['Audience filters pick who sees each one.'],
    related: ['announcements'],
  },
  {
    id: 'setup-assistant', section: 'admin', parent: 'admin-dashboard', name: 'Setup assistant', art: 'robot', admin: true,
    line: 'Set up competitions, memberships and bookings by conversation.',
    aliases: ['ai setup', 'assistant', 'set up with the assistant', 'proposal'],
    what: 'A conversation that produces the same setup as the forms: competitions, membership types, prices, periods, booking hours, who can book. It proposes; the admin applies.',
    holds: ['Sessions, focused on competitions, memberships, bookings or a full setup', 'Proposals', 'Feature requests it could not meet'],
    sees: 'Club admins', manages: 'Club admins; switched on for a club by Tap Leagues',
    rules: ['It never drafts on top of live records: a proposal is applied all at once, now or on a date, with conflicts flagged.', 'Undo for 7 days if nobody has joined, paid or booked against it.', 'It sees the club\'s set-up and counts, never names, emails or health answers.', 'Payment account first: paid items cannot publish until charges are enabled.'],
    related: ['proposal', 'membership-type', 'area-group', 'setup-guide'],
  },
  {
    id: 'proposal', section: 'admin', parent: 'setup-assistant', name: 'Proposals', art: 'clipboard', admin: true,
    line: 'New, change and remove steps, applied together.',
    aliases: ['check page', 'apply', 'undo', 'steps'],
    what: 'What a session proposes: each step creates, changes or removes one live record, with the version it saw. Review on the check page, apply, undo within 7 days.',
    holds: ['Steps grouped by section', 'Applied, undone or discarded, with a report', 'Edit in form for any step'],
    sees: 'Club admins', manages: 'Club admins',
    rules: ['Every step runs the same checks as the manual forms.', 'Resuming folds in what changed since.'],
    related: ['setup-assistant', 'membership-type', 'booking-hours'],
  },
  {
    id: 'archive', section: 'admin', parent: 'admin', name: 'Archive', art: 'archive',
    line: 'Competitions archive instead of being deleted.',
    aliases: ['archived', 'restore', 'delete competition', 'ended'],
    what: 'Leagues, ladders, tournaments, americano leagues, meets and scoring systems are archived with their history and can be restored.',
    holds: ['Archived at per competition'],
    sees: 'Club admins (archived list); members see ended competitions', manages: 'Club admins',
    rules: ['Archived competitions show as ended to players.'],
    related: ['league', 'ladder', 'tournament', 'americano', 'meet'],
  },
  {
    id: 'pdf-export', section: 'admin', parent: 'admin', name: 'Printable tables & PDF', art: 'document',
    line: 'Standings, draws and posters to print.',
    aliases: ['print', 'pdf', 'poster', 'noticeboard'],
    what: 'Tables, draws, meet printouts and QR posters as PDFs for the noticeboard.',
    holds: ['League tables, brackets, meet registers, join posters'],
    sees: 'Club admins (and members for public tables)', manages: 'Club admins',
    rules: ['Printouts respect name privacy like every surface.'],
    related: ['league-standings', 'bracket', 'join-link'],
  },

];

// Connections across the map (drawn lighter than the holds links). `card` reads
// from `from` to `to`: a player holds several memberships, a widget shows one competition.
export const SYSTEM_MAP_LINKS = [
  {from: 'players', to: 'membership', label: 'holds', say: 'A player holds their memberships and passes'},
  {from: 'players', to: 'league-team', label: 'plays in', say: 'Players play in box league teams'},
  {from: 'players', to: 'ladder-positions', label: 'stands at', say: 'Players stand at ladder positions'},
  {from: 'players', to: 'americano-seats', label: 'sits in', say: 'Players sit in americano seats'},
  {from: 'players', to: 'tournament-entries', label: 'enters', say: 'Players enter tournaments'},
  {from: 'players', to: 'meet-players', label: 'joins', say: 'Players join meets'},
  {from: 'players', to: 'booking', label: 'books', say: 'Players book playing areas'},
  {from: 'players', to: 'chats', label: 'chats in', say: 'Players talk in chats'},
  {from: 'players', to: 'charges', label: 'pays', say: 'Players pay charges'},
  {from: 'account', to: 'saved-cards', label: 'saves', say: 'An account keeps saved cards and wallets'},
  {from: 'account', to: 'notifications', label: 'receives', say: 'An account receives notifications'},
  {from: 'club', to: 'players', label: 'has', say: 'A club has its players'},
  {from: 'club', to: 'league', label: 'runs', say: 'A club runs box leagues'},
  {from: 'club', to: 'ladder', label: 'runs', say: 'A club runs ladders'},
  {from: 'club', to: 'tournament', label: 'runs', say: 'A club runs tournaments'},
  {from: 'club', to: 'americano', label: 'runs', say: 'A club runs americano leagues'},
  {from: 'club', to: 'meet', label: 'runs', say: 'A club runs meets'},
  {from: 'club', to: 'playing-areas', label: 'has', say: 'A club has playing areas'},
  {from: 'club', to: 'payments', label: 'takes', say: 'A club takes payments'},
  {from: 'club', to: 'memberships', label: 'sells', say: 'A club sells memberships and passes'},
  {from: 'club', to: 'website', label: 'shows on', say: 'A club shows on its own website'},
  {from: 'club', to: 'tv', label: 'shows on', say: 'A club shows on Clubhouse TV'},
  {from: 'club', to: 'admin', label: 'run by', say: 'A club is run by its admins'},
  {from: 'join-link', to: 'league-cycle', label: 'joins', say: 'An invite link lets people join a box league cycle'},
  {from: 'join-link', to: 'ladder', label: 'joins', say: 'An invite link lets people join a ladder'},
  {from: 'join-link', to: 'tournament', label: 'joins', say: 'An invite link lets people join a tournament'},
  {from: 'join-link', to: 'americano-cycle', label: 'joins', say: 'An invite link lets people join an americano cycle'},
  {from: 'join-link', to: 'meet', label: 'joins', say: 'An invite link lets people join a meet'},
  {from: 'placed', to: 'league-division', label: 'placed into', say: 'From the waiting area people are placed into a division'},
  {from: 'placed', to: 'americano-seats', label: 'placed into', say: 'From the waiting area people are placed into a seat'},
  {from: 'placed', to: 'ladder-positions', label: 'placed into', say: 'From the waiting area people are placed at a ladder position'},
  {from: 'league-matches', to: 'match', label: 'is a', say: 'A box league match is a match like any other'},
  {from: 'challenge-match', to: 'match', label: 'is a', say: 'A challenge match is a match like any other'},
  {from: 'stage-matches', to: 'match', label: 'is a', say: 'A tournament match is a match like any other'},
  {from: 'americano-matches', to: 'match', label: 'is a', say: 'An americano match is a match like any other'},
  {from: 'meet-matches', to: 'match', label: 'is a', say: 'A meet match is a match like any other'},
  {from: 'match', to: 'playing-areas', label: 'played on', say: 'A match is played on a playing area'},
  {from: 'scoring-systems', to: 'league', label: 'scores', say: 'A scoring system decides how box league matches are scored'},
  {from: 'scoring-systems', to: 'ladder', label: 'scores', say: 'A scoring system decides how ladder matches are scored'},
  {from: 'scoring-systems', to: 'tournament', label: 'scores', say: 'A scoring system decides how tournament matches are scored'},
  {from: 'scoring-systems', to: 'americano', label: 'scores', say: 'A scoring system decides how americano matches are scored'},
  {from: 'scoring-systems', to: 'meet', label: 'scores', say: 'A scoring system decides how meet matches are scored'},
  {from: 'handicaps', to: 'league', label: 'set on', say: 'Handicaps can be set on a box league'},
  {from: 'handicaps', to: 'ladder', label: 'set on', say: 'Handicaps can be set on a ladder'},
  {from: 'handicaps', to: 'tournament', label: 'set on', say: 'Handicaps can be set on a tournament'},
  {from: 'handicaps', to: 'americano', label: 'set on', say: 'Handicaps can be set on an americano league'},
  {from: 'result-approval', to: 'player-stats', label: 'feeds', say: 'Approved results feed player stats'},
  {from: 'result-approval', to: 'home-feed', label: 'appears in', say: 'Approved results appear in the home feed'},
  {from: 'live-scoring', to: 'tv-display', label: 'shown on', say: 'Live scores show on Clubhouse TV'},
  {from: 'entry-fees', to: 'league', label: 'charged by', say: 'A box league can charge an entry fee'},
  {from: 'entry-fees', to: 'ladder', label: 'charged by', say: 'A ladder can charge an entry fee'},
  {from: 'entry-fees', to: 'tournament', label: 'charged by', say: 'A tournament can charge an entry fee'},
  {from: 'entry-fees', to: 'americano', label: 'charged by', say: 'An americano league can charge an entry fee'},
  {from: 'session-fees', to: 'meet-occurrence', label: 'bills', say: 'Each meet date bills its session fee'},
  {from: 'membership-period', to: 'charges', label: 'paid by', say: 'A billing period is paid by a charge'},
  {from: 'booking', to: 'charges', label: 'paid by', say: 'A booking is paid by a charge'},
  {from: 'bill-to', to: 'charges', label: 'redirects', say: 'Pay for someone else sends the charge to the payer'},
  {from: 'competition-rules', to: 'league', label: 'gates', say: 'Entry requirements decide who may enter a box league'},
  {from: 'competition-rules', to: 'ladder', label: 'gates', say: 'Entry requirements decide who may enter a ladder'},
  {from: 'competition-rules', to: 'tournament', label: 'gates', say: 'Entry requirements decide who may enter a tournament'},
  {from: 'competition-rules', to: 'americano', label: 'gates', say: 'Entry requirements decide who may enter an americano league'},
  {from: 'competition-rules', to: 'meet', label: 'gates', say: 'Entry requirements decide who may enter a meet'},
  {from: 'membership-type', to: 'who-can-book', label: 'priced in', say: 'Who can book prices bookings by membership type'},
  {from: 'membership-type', to: 'meet', label: 'can cover', say: 'A membership or pass can cover meets'},
  {from: 'scheduling', to: 'match-booking', label: 'creates', say: 'Scheduling a match creates its booking'},
  {from: 'challenge', to: 'match-booking', label: 'may hold', say: 'A challenge may hold a booking for its match'},
  {from: 'meet-occurrence', to: 'booking', label: 'holds areas', say: 'A meet date holds the playing areas it books'},
  {from: 'widgets-competition', to: 'league-standings', label: 'shows', say: 'The competition widget shows box league standings'},
  {from: 'widgets-competition', to: 'ladder-positions', label: 'shows', say: 'The competition widget shows ladder positions'},
  {from: 'widgets-competition', to: 'bracket', label: 'shows', say: 'The competition widget shows the bracket'},
  {from: 'widgets-competition', to: 'americano-standings', label: 'shows', say: 'The competition widget shows americano standings'},
  {from: 'widgets-competition', to: 'match', label: 'shows recent', say: 'The competition widget shows recent matches'},
  {from: 'widgets-stats', to: 'stats', label: 'shows', say: 'The stats widget shows the leaderboards'},
  {from: 'booking-widgets', to: 'booking', label: 'opens', say: 'The booking widget opens bookings'},
  {from: 'booking-widgets', to: 'playing-areas', label: 'shows', say: 'The booking widget shows the playing areas'},
  {from: 'join-widget', to: 'membership-type', label: 'shows', say: 'The join widget shows the membership types and passes'},
  {from: 'tv-panels', to: 'league-standings', label: 'shows', say: 'TV panels show box league standings'},
  {from: 'tv-panels', to: 'ladder-positions', label: 'shows', say: 'TV panels show ladder positions'},
  {from: 'tv-panels', to: 'bracket', label: 'shows', say: 'TV panels show the bracket'},
  {from: 'tv-panels', to: 'meet-occurrence', label: 'shows coming up', say: 'TV panels show the meets coming up'},
  {from: 'tv-display', to: 'join-link', label: 'shows QR', say: 'A TV display shows the invite link as a QR code'},
  {from: 'chats', to: 'league-division', label: 'for each', say: 'Every division has its own chat'},
  {from: 'chats', to: 'meet', label: 'for each', say: 'Every meet has its own chat'},
  {from: 'challenge-chat', to: 'chats', label: 'is a', say: 'A challenge chat is a chat like any other'},
  {from: 'notifications', to: 'challenge', label: 'about', say: 'Notifications tell you about challenges'},
  {from: 'notifications', to: 'meet-rsvp', label: 'about', say: 'Notifications tell you about RSVPs'},
  {from: 'announcements-admin', to: 'announcements', label: 'writes', say: 'Admins write announcements'},
  {from: 'message-reports', to: 'reports-admin', label: 'reviewed in', say: 'Message reports are reviewed in Reports'},
  {from: 'setup-assistant', to: 'membership-type', label: 'proposes', say: 'The setup assistant proposes membership types and passes'},
  {from: 'setup-assistant', to: 'area-group', label: 'proposes', say: 'The setup assistant proposes booking rules'},
  {from: 'setup-assistant', to: 'playing-areas', label: 'proposes', say: 'The setup assistant proposes playing areas'},
  {from: 'parent-consent', to: 'juniors', label: 'protects', say: 'Parent or carer consent protects juniors'},
  {from: 'payment-request', to: 'pay-links', label: 'uses', say: 'Asking someone else to pay uses a pay link'},
  {from: 'membership-import', to: 'member-import', label: 'like', say: 'Membership import works like importing players'},
  {from: 'archive', to: 'league', label: 'archives', say: 'Archive puts a finished box league away'},
  {from: 'admin-dashboard', to: 'join-requests', label: 'lists', say: 'The dashboard lists join requests'},
  {from: 'admin-dashboard', to: 'waiting-area', label: 'lists', say: 'The dashboard lists the waiting area'},
  {from: 'players-admin', to: 'players', label: 'manages', say: 'Players admin manages the players'},
  {from: 'profile-privacy', to: 'public-pages', label: 'locks', say: 'Profile privacy locks a player out of public pages'},
  {from: 'club-visibility', to: 'public-pages', label: 'locks', say: 'Who can see your club locks its public pages'},
  {from: 'link-back', to: 'club-page', label: 'opens', say: 'A link back opens the club page'},
  {from: 'sign-up', to: 'join-link', label: 'starts from', say: 'Signing up often starts from an invite link'},
  {from: 'mobile-app', to: 'notifications', label: 'receives', say: 'The app receives push notifications'},
  {from: 'help-centre', to: 'league', label: 'explains', say: 'The help centre explains box leagues'},
  {from: 'help-centre', to: 'ladder', label: 'explains', say: 'The help centre explains ladders'},
  {from: 'help-centre', to: 'tournament', label: 'explains', say: 'The help centre explains tournaments'},
  {from: 'help-centre', to: 'americano', label: 'explains', say: 'The help centre explains americano leagues'},
  {from: 'help-centre', to: 'meet', label: 'explains', say: 'The help centre explains meets'},
  {from: 'team-names', to: 'league-team', label: 'names', say: 'Team names apply to box league teams'},
  {from: 'team-names', to: 'tournament-entries', label: 'names', say: 'Team names apply to tournament entries'},
  {from: 'team-names', to: 'americano-seats', label: 'names', say: 'Team names apply to americano seats'},
  {from: 'meet-price-change', to: 'session-fees', label: 'changes', say: 'A price change alters the meet session fee'},
];

// The order things happen in: `to` cannot exist or happen without `from` (a
// scoring system before a competition's matches, teams before their matches,
// a Stripe account before anybody pays for someone else). A HARD requirement
// only: the usual route, an optional extra or one of several ways in is not a
// step (a join link is a shortcut to signing up, not needed for it; a join
// request can be made from the club page without a link; consent, health
// questions and a payer are conditions some memberships have; a free price
// option or booking audience needs no Stripe account; a type with no price
// option can still be applied for; a walkover result carries no games; the
// cycle wizard is one way to make divisions, the cycle editor is another; an
// admin may book outside the booking hours; a knockout-only bracket comes
// straight from the draw; a widget's embed code exists before any type does;
// R51 M3: a ladder position changes on a declined or expired challenge, an
// inactivity drop or an admin move, with no match; the home feed carries
// challenges, cards and announcements before any result is approved; a bye
// goes through a tournament stage with no match; a box league or americano
// table exists from the teams or seats alone, everyone on 0, before a match
// is played: none of those are steps).
// Drawn inside an area as a step connector with an arrowhead, routed around
// the other cards, laid out so the flow reads left to right and top to
// bottom, and listed in a part's dialog as "Needs first" and "Comes next".
// Across areas it shows as a chip labelled Needs first / Comes next. Never
// between a part and one it holds (that is the holds link already), never a
// cycle.
export const SYSTEM_MAP_STEPS = [
  // People & accounts
  {from: 'sign-up', to: 'sign-in-methods'},
  // Box leagues
  {from: 'league-team', to: 'league-matches'},
  {from: 'league-standings', to: 'rollover'},
  // Ladders
  {from: 'ladder-rules', to: 'challenge'},
  {from: 'challenge-accept', to: 'challenge-match'},
  // Tournaments
  {from: 'tournament-entries', to: 'draw'},
  {from: 'draw', to: 'stage-matches'},
  // Americano leagues
  {from: 'americano-seats', to: 'americano-rounds'},
  // Meets
  {from: 'register', to: 'meet-matches'},
  // Memberships & passes
  {from: 'membership-settings', to: 'membership-type'},
  {from: 'membership-type', to: 'competition-rules'},
  // Playing areas & bookings
  {from: 'booking-settings', to: 'booking'},
  // Across areas
  {from: 'scoring-systems', to: 'league-matches'},
  {from: 'scoring-systems', to: 'stage-matches'},
  {from: 'scoring-systems', to: 'challenge-match'},
  {from: 'scoring-systems', to: 'americano-matches'},
  {from: 'scoring-systems', to: 'meet-matches'},
  {from: 'payment-account', to: 'bill-to'},
  {from: 'result-approval', to: 'player-stats'},
];

const nodeById = new Map(SYSTEM_MAP_NODES.map((n) => [n.id, n]));
const sectionById = new Map(SYSTEM_MAP_SECTIONS.map((s) => [s.id, s]));
const groupById = new Map(SYSTEM_MAP_GROUPS.map((g) => [g.id, g]));
const groupBySection = new Map(SYSTEM_MAP_GROUPS.flatMap((g) => g.areas.map((a) => [a, g])));

export function findSystemMapNode(id) {
  return id ? nodeById.get(id) || null : null;
}

export function findSystemMapSection(id) {
  return id ? sectionById.get(id) || null : null;
}

export function findSystemMapGroup(id) {
  return id ? groupById.get(id) || null : null;
}

/** The group an area belongs to (every area is in exactly one). */
export function systemMapGroupOf(sectionId) {
  return sectionId ? groupBySection.get(sectionId) || null : null;
}

/** A group's areas, in the group's order, as sections. */
export function systemMapGroupSections(groupId) {
  const g = groupById.get(groupId);
  return g ? g.areas.map((a) => sectionById.get(a)).filter(Boolean) : [];
}

export function systemMapChildren(id) {
  return SYSTEM_MAP_NODES.filter((n) => n.parent === id);
}

/** The section's root node. */
export function systemMapRoot(sectionId) {
  return SYSTEM_MAP_NODES.find((n) => n.section === sectionId && n.parent === null) || null;
}

/** The section's (or group's) tint and ink: the five formats keep their own, a group carries its own; everything else is neutral. */
export function sectionTone(section) {
  const f = section?.format ? formatExplainers[section.format] : null;
  if (f) return {tint: f.tintHex, ink: f.inkHex};
  if (section?.tint && section?.ink) return {tint: section.tint, ink: section.ink};
  if (section?.id === 'memberships') return {tint: '#FBEDF0', ink: '#D32946'};
  return {tint: '#F3F3F3', ink: '#5B5B5B'};
}

/** A node is an admin feature when it, any ancestor, or its section is flagged. */
export function effectiveFlags(node) {
  let admin = false;
  let cur = node;
  while (cur) {
    admin = admin || !!cur.admin;
    cur = cur.parent ? nodeById.get(cur.parent) : null;
  }
  const section = sectionById.get(node?.section);
  if (section) admin = admin || !!section.admin;
  return {admin};
}

export function isAdminFeature(id) {
  const node = nodeById.get(id);
  return node ? effectiveFlags(node).admin : false;
}

/** Which nodes show under the one checkbox. A hidden node hides everything it holds. */
export function visibleSystemMapIds({admin = true} = {}) {
  const out = new Set();
  for (const node of SYSTEM_MAP_NODES) {
    if (!admin && effectiveFlags(node).admin) continue;
    out.add(node.id);
  }
  return out;
}

const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

/**
 * Search names, aliases and descriptions. Name matches rank first, then aliases,
 * then the one-line and longer text. Returns nodes, best first.
 */
export function searchSystemMap(query, {within = null} = {}) {
  const q = norm(query).trim();
  if (q.length < 2) return [];
  const terms = q.split(/\s+/).filter(Boolean);
  const scored = [];
  for (const node of SYSTEM_MAP_NODES) {
    if (within && !within.has(node.id)) continue;
    const name = norm(node.name);
    const aliases = (node.aliases || []).map(norm);
    const text = norm([node.line, node.what, ...(node.holds || []), ...(node.rules || [])].join(' '));
    // The whole query against the name first, so "find a club" beats "club"
    let score = 0;
    if (name === q) score += 200;
    else if (name.startsWith(q)) score += 120;
    else if (name.includes(q)) score += 80;
    else if (aliases.some((a) => a === q)) score += 150;
    for (const term of terms) {
      if (name === term) score += 100;
      else if (name.startsWith(term)) score += 60;
      else if (name.includes(term)) score += 40;
      else if (aliases.some((a) => a === term)) score += 50;
      else if (aliases.some((a) => a.includes(term))) score += 30;
      else if (text.includes(term)) score += 10;
      else { score = 0; break; }
    }
    if (score > 0) scored.push({node, score});
  }
  scored.sort((a, b) => b.score - a.score || a.node.name.localeCompare(b.node.name));
  return scored.map((s) => s.node);
}

/** Every connection a node takes part in: the holds links to and from it, and the cross links. */
export function systemMapConnections(id) {
  const node = nodeById.get(id);
  if (!node) return [];
  const out = [];
  if (node.parent) out.push({id: node.parent, label: 'held by', kind: 'holds'});
  for (const child of systemMapChildren(id)) out.push({id: child.id, label: 'holds', kind: 'holds'});
  for (const link of SYSTEM_MAP_LINKS) {
    if (link.from === id) out.push({id: link.to, label: link.label, kind: 'link', ...(link.say ? {say: link.say} : {})});
    else if (link.to === id) out.push({id: link.from, label: link.label, kind: 'link', reverse: true, ...(link.say ? {say: link.say} : {})});
  }
  return out;
}

/**
 * A part's place in the order of things: what needs to exist or be done
 * before it (`before`) and what it comes before (`after`), from SYSTEM_MAP_STEPS.
 */
export function systemMapSteps(id) {
  const before = [], after = [];
  for (const st of SYSTEM_MAP_STEPS) {
    if (st.to === id && nodeById.has(st.from)) before.push({id: st.from, kind: 'step', label: 'needs first'});
    else if (st.from === id && nodeById.has(st.to)) after.push({id: st.to, kind: 'step', label: 'comes next', reverse: true});
  }
  return {before, after};
}

/**
 * A part's links to parts in OTHER areas, for the chips beside a hovered or
 * selected card and the dialog's "Connected to": the steps first (needs first,
 * comes next), then the connections. Each carries the other part, its area and
 * the area's tone, so a chip can be drawn without more lookups.
 */
export function crossAreaLinks(id, {within = null} = {}) {
  const node = nodeById.get(id);
  if (!node) return [];
  const steps = systemMapSteps(id);
  return [...steps.before, ...steps.after, ...systemMapConnections(id).filter((c) => c.kind === 'link')]
      .map((c) => ({...c, node: nodeById.get(c.id)}))
      .filter((c) => c.node && c.node.section !== node.section && (!within || within.has(c.id)))
      .map((c) => {
        const section = sectionById.get(c.node.section);
        return {...c, section, tone: sectionTone(section)};
      });
}

/**
 * The main relationships between AREAS, for the few soft connectors on the
 * overview: section pairs ranked by how many connections join them.
 */
export function areaRelations({limit = 12, min = 2} = {}) {
  const counts = new Map();
  for (const l of SYSTEM_MAP_LINKS) {
    const a = nodeById.get(l.from)?.section, b = nodeById.get(l.to)?.section;
    if (!a || !b || a === b) continue;
    const key = [a, b].sort().join('>');
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  return [...counts.entries()]
      .map(([key, count]) => { const [a, b] = key.split('>'); return {a, b, count}; })
      .filter((r) => r.count >= min)
      .sort((x, y) => y.count - x.count || x.a.localeCompare(y.a) || x.b.localeCompare(y.b))
      .slice(0, limit);
}

/** Words that never appear in visible copy (aliases are search-only and may keep the old words). */
export const BANNED_COPY = Object.freeze([
  {re: /\broster\b/i, say: 'Players'},
  {re: /\bwalk-?ins?\b/i, say: 'people who turned up'},
  {re: /\bfixtures?\b/i, say: 'matches'},
  {re: /\bmembers[- ]only\b/i, say: 'Membership or pass needed'},
  {re: /\barea groups?\b/i, say: 'Booking rules'},
  {re: /\bsuper[- ]?admins?\b/i, say: 'Tap Leagues'},
  {re: /\bcoming next\b/i, say: 'nothing: everything on the map is live'},
  {re: /\bN (days|places|people|sessions)\b/, say: 'a set number of'},
  {re: /\bconsole\b/i, say: 'register'},
]);

const COPY_FIELDS = ['name', 'line', 'what', 'sees', 'manages'];
const COPY_LISTS = ['holds', 'rules'];

/** Visible copy that breaks the word rules, for the test and a quick check in dev. */
export function systemMapCopyProblems() {
  const problems = [];
  const check = (where, text) => {
    for (const rule of BANNED_COPY) {
      const m = rule.re.exec(text || '');
      if (m) problems.push(`${where}: "${m[0]}" (say ${rule.say})`);
    }
  };
  for (const s of SYSTEM_MAP_SECTIONS) {
    check(`section ${s.id}.name`, s.name);
    check(`section ${s.id}.line`, s.line);
  }
  for (const g of SYSTEM_MAP_GROUPS) check(`group ${g.id}.name`, g.name);
  for (const n of SYSTEM_MAP_NODES) {
    for (const f of COPY_FIELDS) check(`${n.id}.${f}`, n[f]);
    for (const f of COPY_LISTS) for (const item of n[f] || []) check(`${n.id}.${f}`, item);
  }
  return problems;
}

/** Problems with the data, for the test and for a quick sanity check in dev. */
export function validateSystemMap() {
  const problems = [];
  const ids = new Set();
  const sectionIds = new Set(SYSTEM_MAP_SECTIONS.map((s) => s.id));
  for (const s of SYSTEM_MAP_SECTIONS) if (ids.has(s.id)) problems.push(`duplicate section id ${s.id}`); else ids.add(s.id);
  const seen = new Set();
  for (const n of SYSTEM_MAP_NODES) {
    if (seen.has(n.id)) problems.push(`duplicate node id ${n.id}`);
    seen.add(n.id);
    if (!sectionIds.has(n.section)) problems.push(`${n.id}: unknown section ${n.section}`);
    if (!/^[a-z0-9-]+$/.test(n.id)) problems.push(`${n.id}: id must be a bare token`);
    if (n.parent !== null) {
      const parent = nodeById.get(n.parent);
      if (!parent) problems.push(`${n.id}: unknown parent ${n.parent}`);
      else if (parent.section !== n.section) problems.push(`${n.id}: parent ${n.parent} is in another section`);
    }
    for (const key of ['super', 'next', 'card']) if (key in n) problems.push(`${n.id}: ${key} is no longer a field`);
    if (n.siblings !== undefined && n.siblings !== true) problems.push(`${n.id}: siblings is a reason flag, true or absent`);
    if (n.admin !== undefined && typeof n.admin !== 'boolean') problems.push(`${n.id}: admin must be boolean`);
    for (const key of ['name', 'line', 'art', 'what', 'sees', 'manages']) {
      if (typeof n[key] !== 'string' || !n[key]) problems.push(`${n.id}: missing ${key}`);
    }
    for (const key of ['aliases', 'holds', 'rules', 'related']) {
      if (!Array.isArray(n[key])) problems.push(`${n.id}: ${key} must be a list`);
    }
    for (const r of n.related || []) if (!nodeById.has(r)) problems.push(`${n.id}: related ${r} does not exist`);
    if (/fixture/i.test(n.name)) problems.push(`${n.id}: say matches, not fixtures`);
  }
  for (const s of SYSTEM_MAP_SECTIONS) {
    const roots = SYSTEM_MAP_NODES.filter((n) => n.section === s.id && n.parent === null);
    if (roots.length !== 1) problems.push(`section ${s.id} must have exactly one root (has ${roots.length})`);
    for (const key of ['super', 'next']) if (key in s) problems.push(`section ${s.id}: ${key} is no longer a flag`);
  }
  // Groups: every area in exactly one, ids that are deep links of their own
  const grouped = new Map();
  for (const g of SYSTEM_MAP_GROUPS) {
    if (!/^[a-z0-9-]+$/.test(g.id)) problems.push(`group ${g.id}: id must be a bare token`);
    if (sectionIds.has(g.id) || seen.has(g.id)) problems.push(`group ${g.id}: id clashes with an area or a part`);
    for (const key of ['name', 'art', 'tint', 'ink']) if (typeof g[key] !== 'string' || !g[key]) problems.push(`group ${g.id}: missing ${key}`);
    if (!Array.isArray(g.areas) || !g.areas.length) problems.push(`group ${g.id}: holds no areas`);
    for (const a of g.areas || []) {
      if (!sectionIds.has(a)) problems.push(`group ${g.id}: unknown area ${a}`);
      grouped.set(a, (grouped.get(a) || 0) + 1);
    }
  }
  for (const s of SYSTEM_MAP_SECTIONS) {
    const n = grouped.get(s.id) || 0;
    if (n !== 1) problems.push(`section ${s.id} is in ${n} groups (must be exactly one)`);
  }
  const linkKeys = new Set();
  for (const l of SYSTEM_MAP_LINKS) {
    if (!nodeById.has(l.from)) problems.push(`link from unknown ${l.from}`);
    if (!nodeById.has(l.to)) problems.push(`link to unknown ${l.to}`);
    if ('card' in l) problems.push(`link ${l.from}>${l.to}: card is no longer a field`);
    const key = `${l.from}>${l.to}`;
    if (linkKeys.has(key)) problems.push(`duplicate link ${key}`);
    linkKeys.add(key);
    const a = nodeById.get(l.from), b = nodeById.get(l.to);
    if (a && b && (a.parent === b.id || b.parent === a.id)) problems.push(`link ${key} duplicates a holds link`);
  }
  const stepKeys = new Set();
  const isAncestor = (up, id) => { for (let n = nodeById.get(id); n; n = nodeById.get(n.parent)) if (n.parent === up) return true; return false; };
  for (const st of SYSTEM_MAP_STEPS) {
    const key = `${st.from}>${st.to}`;
    if (!nodeById.has(st.from)) problems.push(`step from unknown ${st.from}`);
    if (!nodeById.has(st.to)) problems.push(`step to unknown ${st.to}`);
    if (st.from === st.to) problems.push(`step ${key} joins a part to itself`);
    if (stepKeys.has(key)) problems.push(`duplicate step ${key}`);
    stepKeys.add(key);
    if (nodeById.has(st.from) && nodeById.has(st.to) && (isAncestor(st.from, st.to) || isAncestor(st.to, st.from))) problems.push(`step ${key} runs along a holds chain`);
  }
  for (const id of systemMapStepCycle()) problems.push(`steps form a cycle through ${id}`);
  return problems;
}

/** The parts on a cycle in the steps (none when the order is sound). */
export function systemMapStepCycle() {
  const next = new Map();
  for (const st of SYSTEM_MAP_STEPS) {
    if (!next.has(st.from)) next.set(st.from, []);
    next.get(st.from).push(st.to);
  }
  const state = new Map();
  const stack = [];
  let found = null;
  const visit = (id) => {
    if (found) return;
    state.set(id, 1);
    stack.push(id);
    for (const to of next.get(id) || []) {
      if (state.get(to) === 1) { found = stack.slice(stack.indexOf(to)); return; }
      if (!state.has(to)) visit(to);
    }
    stack.pop();
    state.set(id, 2);
  };
  for (const id of next.keys()) if (!state.has(id)) visit(id);
  return found || [];
}
